import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import { toast } from "sonner";
import {
  Camera, Spinner, ArrowLeft, ArrowRight,
  WhatsappLogo, DownloadSimple, ArrowsHorizontal, X, Sparkle, Share, Image as ImageIcon,
  House, Check, Sliders, MagnifyingGlassPlus, PencilSimple,
  DotsThreeVertical, ArrowsClockwise, Trash, Eye, ArrowsOutSimple, Question,
  Lightning, Armchair, Broom, MapTrifold, ShieldCheck, HouseLine,
} from "@phosphor-icons/react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { galleryItemUrl, liveFileUrl, liveBeforeAfterCardUrl, liveBeforeAfterVideoUrl } from "@/lib/fotoUrls";
import { PhotoTileIcon } from "@/components/photo/FotoLightboxParts";
import { FotoEditorLightbox } from "@/components/photo/FotoEditorLightbox";
import { ResultViewer } from "@/components/photo/ResultViewer";
import {
  STAGE_STYLES, ROOM_HINTS, MODES, resolveStyle,
} from "@/lib/fotoEditorConstants";
import AppShell from "@/components/AppShell";
import DismissibleIntro from "@/components/DismissibleIntro";
import ReopenGuidesBtn from "@/components/ReopenGuidesBtn";
import SectionOnboarding from "@/components/SectionOnboarding";
import RegenerateButton from "@/components/RegenerateButton";
import { shareImage, shareVideo } from "@/lib/share";
import { probePhotoQuality } from "@/lib/photoQuality";
import { isReaderMode } from "@/lib/iosReader";
import { isIOS } from "@/lib/native";
import { useBgJobs } from "@/components/jobs/useBgJobs";
// iter r97e · User M1520: recovery al foreground quando iOS sospende WKWebView
import {
  newRequestId, withWakeLock, recoverArtifact,
  trackPendingAiOp, clearPendingAiOp, listPendingAiOps,
} from "@/lib/aiWakeGuard";

// iter 275 round 33: helper URL spostati in /app/frontend/src/lib/fotoUrls.js
// per essere condivisi con GalleriaFoto.jsx. Pure extract.
//
// iter v28 r372: ResultViewer estratto in /components/photo/ResultViewer.jsx
// e le costanti (STAGE_STYLES, ROOM_HINTS, MODES, resolveStyle) in
// /lib/fotoEditorConstants.js — vedi import sopra.

export default function FotoEditor() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // iter 255 — Reedit deep-link: il picker della galleria può rimandare qui
  // con ?reedit={liveId}&return={url} per rimodificare una foto e poi tornare.
  const reeditId = searchParams.get("reedit");
  const reeditPath = searchParams.get("reeditPath"); // iter 259: foto da PropertyDetail (path diretto, non live id)
  // iter v26 r7 — reeditMany: comma-separated lista di photo IDs (formato
  // "live:xxx" o "prop:xxx") da rilavorare in batch. Caricate dalla sticky
  // bar Galleria foto → bottone "Riprova" quando ci sono N selezioni.
  const reeditMany = searchParams.get("reeditMany");
  const reeditMode = searchParams.get("reeditMode"); // opzionale: forza il mode (enhance/stage/restyle/furnish_plan)
  const returnUrl = searchParams.get("return");
  const { user } = useAuth();
  const bgJobs = useBgJobs();
  const fileInputRef = useRef(null);
  // iter v32 r12 — User M386 BUG FIX: bug di concorrenza su "Arricchimento
  // da stanza" / Genera. Click rapidi multipli partivano prima che lo
  // state `anyProcessing` si propagasse (setItems è async) → l'utente
  // veniva addebitato 2 o 3 volte e generavamo render duplicati.
  // Doppia barriera: ref-based (sincrono, blocca dentro runAi) + state
  // (disabilita il bottone immediatamente al primo tap senza aspettare
  // il re-render).
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  // iter v28 r449 — User M1363: tap su "Parti da qui" non dava feedback
  // visivo (la zona uploader era fuori viewport, soprattutto su mobile).
  // Tengo un ref sull'uploader per scrollarci automaticamente al cambio mode.
  const uploaderRef = useRef(null);

  const [mode, setMode] = useState("enhance");
  // iter v28 r366 — User M1276: 2 modalità per "Migliora" (la privacy quadri
  // è SEMPRE attiva in entrambe — fa parte del prompt base).
  // iter v28 r409 — User M1318: «lascia solo il migliora tutto». Modalità
  // unica = "completo" (luce, contrasto, linee dritte, riordino, privacy
  // quadri). Niente più switch o state, hard-coded.
  // iter v33 r107 · User: reintrodotto un solo segmented Completo | Solo luce.
  // Default Completo (comportamento invariato). "solo_luce" → focus=keep_objects
  // backend (preserva scena, stessa qualità fotografica pro).
  const [enhanceFocus, setEnhanceFocus] = useState("completo"); // completo | solo_luce
  const buildFocusParam = () => (enhanceFocus === "solo_luce" ? "keep_objects" : "completo");
  const [style, setStyle] = useState("casuale");
  const [room, setRoom] = useState("");
  const [changeFloor, setChangeFloor] = useState(false);
  const [floorStyle, setFloorStyle] = useState("parquet_chiaro");
  // iter v30 r147 — Restyling esterno: toggle "casa piccola" (no piscine
  // inventate, giardino minimal). Default false = casa di taglio medio.
  const [compact, setCompact] = useState(false);
  // iter 2026-02 — Personalizza (opzionale): note aggiuntive dell'utente
  // per Arreda spazio vuoto. Additive alle regole del prompt (max 300 char).
  const [personalizeOpen, setPersonalizeOpen] = useState(false);
  const [personalizeText, setPersonalizeText] = useState("");
  // iter 2026-02 — Multi-foto Arreda: mapping id foto → roomHint scelto;
  // e groups[groupId] = Set<itemId> per raggruppare più viste della stessa stanza.
  // Se una foto NON è in items.roomHint sarà "" (=Automatico).
  const [roomPickerFor, setRoomPickerFor] = useState(null); // itemId | "bulk-selected" | null
  const [selectedForBulk, setSelectedForBulk] = useState(new Set()); // itemIds selezionati per bulk-actions
  const [quota, setQuota] = useState(null);
  // iter v32 r37e — User M1431: banner one-time che avvisa l'utente su iOS
  // che l'app va in pausa se passa ad altre. Mostrato solo su Capacitor iOS,
  // solo se non già visto. Dismiss-on-first-generate per non infastidire.
  const [iosBgBannerVisible, setIosBgBannerVisible] = useState(() => {
    if (!isIOS) return false;
    try { return localStorage.getItem("foto-editor:ios-bg-banner-seen") !== "1"; }
    catch { return false; }
  });
  // iter 305 round 9: cache localStorage per galleria + properties.
  // Quando l'utente rientra in Editor foto le tile della galleria e la
  // lista annunci appaiono ISTANTE dalla cache, poi vengono aggiornate
  // in background dalla rete. Risolve il "pagina bianca per 2-3 secondi"
  // al rientro segnalato dall'utente.
  const [history, setHistory] = useState(() => {
    try {
      const cached = localStorage.getItem("pronto:cache:gallery");
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [properties, setProperties] = useState(() => {
    try {
      const cached = localStorage.getItem("pronto:cache:properties");
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  // Import dialog state: which history photo is being attached to which annuncio.
  const [importingId, setImportingId] = useState(null); // history item id
  const [importBusyPid, setImportBusyPid] = useState(null);
  // "Annuncio Lampo" — modale per creare draft + andare al Social Studio
  // direttamente da una foto singola lavorata.
  const [lampoOpen, setLampoOpen] = useState(false);
  // iter v31 r10 — Sheet "Altre azioni" del risultato singolo: Card,
  // Video 6s, Storia social, Elimina foto.
  const [moreOpen, setMoreOpen] = useState(false);
  const [lampoTitle, setLampoTitle] = useState("");
  const [lampoCity, setLampoCity] = useState("");
  const [lampoPrice, setLampoPrice] = useState("");
  const [lampoBusy, setLampoBusy] = useState(false);
  // Batch state: each item has { id, file, previewUrl, status, result, error }
  // status: "pending" | "processing" | "done" | "error"
  const [items, setItems] = useState([]);
  // iter 275: selezione per-foto. Default: tutte le foto caricate sono
  // selezionate. L'utente può escludere singole foto dal batch toggleando
  // la tile. `selectedIds` contiene gli id ESCLUSI (più semplice mantenere
  // sync con add/remove). Se vuoto → tutte selezionate (comportamento
  // pre-iter 275). Solo le pending NON escluse vengono processate da runAi.
  const [excludedIds, setExcludedIds] = useState(new Set());
  const toggleExcluded = (id) => {
    setExcludedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  // iter 2026-02 — Multi-foto Arreda: "Sono foto della stessa stanza?"
  // toggle globale. Quando ON, le foto con lo stesso roomHint (assegnato
  // per-tile) ricevono il medesimo group_id nel batch → il backend usa
  // la prima riuscita del gruppo come reference per le viste successive.
  const [groupSameRoom, setGroupSameRoom] = useState(false);
  const setItemRoomHint = (id, hint) => {
    setItems((prev) => prev.map((it) => it.id === id ? { ...it, roomHint: hint } : it));
  };
  const batchActive = items.length > 0;
  const allDone = batchActive && items.every((it) => it.status === "done" || it.status === "error");
  const anyProcessing = items.some((it) => it.status === "processing");
  // iter v28 r468 — User M1393: «se uno carica tante foto non si impalli».
  // Counter di progress: quante "done" su quante totali (escluse le excluded).
  const batchToProcess = items.filter((it) => !excludedIds.has(it.id));
  const batchDoneCount = batchToProcess.filter((it) => it.status === "done").length;
  const batchTotalCount = batchToProcess.length;
  // Single-photo mode helpers (used for the result viewer when only 1 file)
  const singleResultItem = items.length === 1 ? items[0] : null;

  const isPro = user?.plan === "pro" || user?.subscription_status === "referral";
  const currentMode = MODES.find((m) => m.v === mode);

  // iter v31 r13 — mappa mode → kind per tray globale "in lavorazione".
  const bgKindForMode = (m) => {
    if (m === "enhance") return "photo_enhance";
    if (m === "restyle") return "photo_restyle";
    if (m === "empty_room") return "photo_empty_room";
    if (m === "furnish_plan") return "floor_plan";
    if (m === "exterior_restyle") return "photo_restyle";
    return "photo_stage";
  };

  useEffect(() => {
    api.get("/live/quota").then(({ data }) => setQuota(data)).catch(() => {});
    // Unified gallery: live previews + enhanced photos from all properties
    api.get("/gallery?limit=100").then(({ data }) => {
      const list = data || [];
      setHistory(list);
      try { localStorage.setItem("pronto:cache:gallery", JSON.stringify(list)); } catch { /* quota full */ }
    }).catch(() => {});
    api.get("/properties").then(({ data }) => {
      const list = data || [];
      setProperties(list);
      try { localStorage.setItem("pronto:cache:properties", JSON.stringify(list)); } catch { /* quota full */ }
    }).catch(() => {});
  }, []);

  // iter v28 r449 — User M1363: «in editor foto, soprattutto da cellulare,
  // ma anche da desktop, appena entri, mi raccomando il tour guidato che
  // deve funzionare». Toast one-shot al primo accesso che spiega cosa fare
  // (tap su una card sotto → scegli lo strumento → poi carichi le foto).
  // Persistito in localStorage, l'agente non lo vede mai più.
  useEffect(() => {
    const KEY = "pronto:foto-editor:first-tour-v1";
    if (localStorage.getItem(KEY)) return;
    const t = setTimeout(() => {
      toast.info(
        "Tap su una card qui sotto per scegliere lo strumento giusto: arreda, svuota, rinnova, migliora o pianta.",
        {
          id: "foto-first-tour",
          duration: 8000,
          icon: <Sparkle size={14} weight="fill" className="text-[#C8A66B]" />,
        },
      );
      localStorage.setItem(KEY, "1");
    }, 900);
    return () => clearTimeout(t);
  }, []);

  // iter 255: deep-link ?reedit=ID — pre-carica nel batch la foto richiesta.
  // Usata dal picker galleria quando l'utente vuole rilavorare prima di
  // aggiungere all'annuncio. Si esegue una volta sola al mount.
  const reeditAppliedRef = useRef(false);
  useEffect(() => {
    if (!reeditId && !reeditPath) return;
    if (reeditAppliedRef.current) return;
    reeditAppliedRef.current = true;
    (async () => {
      try {
        // iter 259 / 309 r17: due modalità di reedit. ENTRAMBE partono
        // SEMPRE dalla foto originale ("before"), mai dall'enhanced. Così
        // ogni nuovo tentativo non stratifica sopra il precedente.
        //  - `?reedit=ID` → foto da galleria globale (/live/previews)
        //  - `?reeditPath=PATH` → riservato (oggi nessun caller). PATH deve
        //    essere SEMPRE il path originale (mai enhanced/staged).
        let src;
        let label;
        if (reeditPath) {
          // Path arriva URL-encoded. Usiamo authedFileUrl-equivalent.
          const token = Cookies.get("vista_jwt");
          const qs = new URLSearchParams({ path: decodeURIComponent(reeditPath) });
          if (token) qs.set("auth", token);
          src = `${API}/files?${qs.toString()}`;
          label = "annuncio";
        } else {
          // iter 305 round 8 — BUG FIX: rilavoriamo dall'ORIGINALE (before),
          // non dall'output già rielaborato (after). Altrimenti i nuovi
          // tentativi si stratificano sopra il risultato precedente.
          src = liveFileUrl(reeditId, "before");
          label = "galleria";
        }
        const res = await fetch(src);
        if (!res.ok) throw new Error("fetch failed");
        const blob = await res.blob();
        const idForName = reeditId || (reeditPath ? reeditPath.split("/").pop().slice(0, 12) : "reedit");
        const file = new File([blob], `pronto-reedit-${idForName}.jpg`, { type: blob.type || "image/jpeg" });
        const previewUrl = URL.createObjectURL(blob);
        setItems([{
          id: `${Date.now()}-reedit`,
          file,
          previewUrl,
          status: "pending",
          result: null,
          error: null,
        }]);
        toast.success(`Foto caricata da ${label} · scegli come rilavorarla`, { duration: 2500 });
      } catch {
        toast.error("Impossibile caricare la foto da rilavorare");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reeditId, reeditPath]);

  // iter v26 r7 — Batch reedit: carica N foto dalla galleria globale come
  // items multipli nell'editor, applicando il `reeditMode` passato (oppure
  // "enhance" default). Ogni foto viene scaricata dalla sua original_url
  // (esposta dal backend gallery API) così l'AI riparte sempre dal nativo.
  const reeditManyAppliedRef = useRef(false);
  useEffect(() => {
    if (!reeditMany) return;
    if (reeditManyAppliedRef.current) return;
    reeditManyAppliedRef.current = true;
    (async () => {
      try {
        // 1. Fetch la galleria per recuperare i metadata (kind + original_url) per ogni id
        const { data: gallery } = await api.get("/gallery?limit=200");
        const wantIds = reeditMany.split(",").filter(Boolean);
        const items_meta = wantIds
          .map((id) => (gallery || []).find((g) => g.id === id))
          .filter(Boolean);
        if (items_meta.length === 0) {
          toast.error("Foto non trovate in galleria");
          return;
        }
        // 2. Mode auto-detect: se reeditMode è esplicito usalo, altrimenti
        //    vedi se TUTTE le foto sono dello stesso kind → usa quello.
        //    Foto miste → "enhance" default + toast warning.
        const VALID_MODES = ["enhance", "stage", "restyle", "furnish_plan"];
        let targetMode = "enhance";
        if (reeditMode && VALID_MODES.includes(reeditMode)) {
          targetMode = reeditMode;
        } else {
          const kinds = [...new Set(items_meta.map((m) => m.kind).filter(Boolean))];
          if (kinds.length === 1 && VALID_MODES.includes(kinds[0])) {
            targetMode = kinds[0];
          } else if (kinds.length > 1) {
            toast.warning("Le foto selezionate hanno tipi diversi — uso 'Migliora' come default. Cambia modalità se serve.");
          }
        }
        setMode(targetMode);
        // 3. Fetch ogni foto come blob → crea items[]
        toast.loading(`Apro ${items_meta.length} foto…`, { id: "reedit-many" });
        const token = Cookies.get("vista_jwt");
        const newItems = [];
        for (const m of items_meta) {
          try {
            const rawUrl = m.original_url || m.url;
            if (!rawUrl) continue;
            const sep = rawUrl.includes("?") ? "&" : "?";
            const tokenParam = token ? `${sep}auth=${token}` : "";
            const cleanPath = rawUrl.startsWith("/api/") ? rawUrl.slice(4) : rawUrl;
            const src = `${API}${cleanPath}${tokenParam}`;
            const res = await fetch(src);
            if (!res.ok) continue;
            const blob = await res.blob();
            const file = new File([blob], `pronto-foto-${(m.id || "").slice(-10)}.jpg`, { type: blob.type || "image/jpeg" });
            const previewUrl = URL.createObjectURL(blob);
            newItems.push({
              id: `${Date.now()}-${m.id}`,
              file,
              previewUrl,
              status: "pending",
              result: null,
              error: null,
            });
          } catch (e) {
            console.warn(`reeditMany: skip ${m.id}`, e);
          }
        }
        if (newItems.length === 0) {
          toast.error("Nessuna foto caricabile", { id: "reedit-many" });
          return;
        }
        // Reset e set come nuovi items
        items.forEach((it) => { if (it.previewUrl) URL.revokeObjectURL(it.previewUrl); });
        setItems(newItems);
        // iter v28 r88 — Reset della selezione galleria: l'utente aveva
        // cliccato "Riprova" su 1+ foto selezionate → la sticky bar in
        // basso rimaneva visibile creando confusione (sembrava ancora in
        // multi-select). Puliamo lo stato dopo aver consumato reeditMany.
        setHistorySelected({});
        setHistorySelectMode(false);
        toast.success(`${newItems.length} foto pronte · scegli come rilavorarle`, { id: "reedit-many", duration: 2500 });
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (e) {
        console.error("reeditMany error", e);
        toast.error("Impossibile caricare le foto dalla galleria", { id: "reedit-many" });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reeditMany, reeditMode]);

  const importToProperty = async (liveId, propertyId) => {
    setImportBusyPid(propertyId);
    try {
      await api.post(`/properties/${propertyId}/photos/import-from-live`, { live_id: liveId });
      toast.success("Foto importata nell'annuncio", { duration: 2000 });
      setImportingId(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Importazione non riuscita");
    } finally {
      setImportBusyPid(null);
    }
  };

  // "Annuncio Lampo": crea un draft con i campi minimi, importa la foto del
  // singolo risultato, redireziona al tab Social del Property Detail. Pensato
  // per chi vuole solo una storia/post da una foto migliorata, senza creare
  // un annuncio completo.
  const createLampo = async () => {
    const liveId = singleResultItem?.result?.id;
    if (!liveId) {
      toast.error("Nessuna foto pronta");
      return;
    }
    if (!lampoTitle.trim()) {
      toast.error("Aggiungi almeno un titolo");
      return;
    }
    setLampoBusy(true);
    try {
      const body = {
        title: lampoTitle.trim(),
        listing_type: "vendita",
        property_type: "Appartamento",
      };
      const city = lampoCity.trim();
      if (city) body.city = city;
      const priceN = parseFloat(String(lampoPrice).replace(/[^\d.,]/g, "").replace(",", "."));
      if (!Number.isNaN(priceN) && priceN > 0) body.price = priceN;
      const { data: created } = await api.post("/properties", body);
      await api.post(`/properties/${created.id}/photos/import-from-live`, { live_id: liveId });
      toast.success("Annuncio lampo creato — apro l'Editor social");
      setLampoOpen(false);
      navigate(`/dashboard/property/${created.id}?tab=social`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Creazione non riuscita");
    } finally {
      setLampoBusy(false);
    }
  };

  const openPicker = () => fileInputRef.current?.click();

  // iter r97e · User M1520 — Recovery al foreground:
  // Quando l'app torna visibile dopo essere stata in background, controllo
  // se ci sono operazioni AI pending in localStorage. Per ognuna, chiedo
  // al backend se ha nel frattempo salvato il risultato. Se sì, aggiorno
  // l'item corrispondente da "error" a "done" senza far ripagare crediti.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const runResume = async () => {
      const pending = listPendingAiOps();
      if (!pending.length) return;
      for (const op of pending) {
        try {
          const rec = await recoverArtifact(api, op.requestId);
          if (!rec?.found) continue;
          // Trovato! Aggiorno l'item con status=done e result=artifact
          setItems((prev) => prev.map((x) => {
            if (x.id !== op.itemId) return x;
            // Se già "done", non toccare (l'utente potrebbe averlo rigenerato)
            if (x.status === "done") return x;
            return {
              ...x,
              status: "done",
              error: null,
              result: {
                id: rec.id,
                before_path: rec.before_path,
                after_path: rec.after_path,
                kind: rec.kind || op.mode,
                style: rec.style,
                recovered: true,
              },
            };
          }));
          clearPendingAiOp(op.requestId);
          toast.success("Generazione recuperata", {
            description: "L'app era in background: ho ripreso il risultato senza rifare l'operazione.",
            duration: 4500,
          });
        } catch { /* ignora, riproveremo alla prossima occasione */ }
      }
    };

    const onVis = () => {
      if (document.visibilityState === "visible") {
        // Piccolo delay per lasciare che il webview iOS finisca di ripristinare
        // le connessioni di rete prima di lanciare la fetch.
        setTimeout(runResume, 800);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    // Anche al mount, prova subito (l'utente potrebbe aver chiuso l'app e
    // riaperto direttamente questa pagina via deep link).
    setTimeout(runResume, 1200);
    return () => document.removeEventListener("visibilitychange", onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const capped = files.slice(0, 10);
    const newItems = capped.map((file, idx) => ({
      id: `${Date.now()}-${idx}-${file.name}`,
      file,
      previewUrl: URL.createObjectURL(file),
      status: "pending",
      result: null,
      error: null,
      // iter 2026-02 — per-foto category (solo mode=stage nel multi-batch).
      // Default "" = Automatico (nessuna directive → il prompt usa lo stile
      // generico). L'utente può cambiarlo dalla tile.
      roomHint: "",
      // iter 2026-02 — group_id: due o più foto DELLO STESSO ambiente. Se
      // due foto hanno lo stesso groupId, la 1ª riuscita è reference della 2ª.
      groupId: null,
    }));
    setItems((prev) => [...prev, ...newItems].slice(0, 10));

    // Non-blocking quality probe — warn the user if foto is dark/blurry/small.
    capped.forEach(async (file) => {
      const probe = await probePhotoQuality(file);
      if (probe?.warnings?.length) {
        const first = probe.warnings[0];
        toast(first.message, {
          description: probe.warnings.length > 1
            ? `+ ${probe.warnings.length - 1} altro suggerimento`
            : undefined,
          duration: 5500,
        });
      }
    });
  };

  const runOne = async (item, opts = {}) => {
    const fd = new FormData();
    fd.append("file", item.file);
    if (mode === "stage" || mode === "restyle" || mode === "furnish_plan") {
      fd.append("style", resolveStyle(style));
    }
    if (mode === "stage" || mode === "restyle") {
      if (room) fd.append("room_hint", room);
    }
    // iter v28 r93 — Stage e Restyle: parametro pavimento condiviso.
    // Stage: passa anche `confirm_not_empty` se l'utente conferma di
    // procedere su una stanza già arredata (gestito da catch 422).
    if (mode === "stage") {
      fd.append("change_floor", String(!!changeFloor));
      if (changeFloor) fd.append("floor_style", floorStyle);
      if (opts.confirmNotEmpty) fd.append("confirm_not_empty", "true");
      // iter v28 r447 — User M1360: coerenza multi-foto. Se nella sessione
      // c'è già una foto staged completata, la passiamo come `style_reference_url`
      // così il modello mantiene palette/materiali/atmosfera coerenti
      // tra tutte le foto dello stesso ambiente.
      if (opts.styleReferenceUrl) {
        fd.append("style_reference_url", opts.styleReferenceUrl);
      }
      // iter 2026-02 — Personalizza (opzionale): note aggiuntive additive.
      if (personalizeText.trim()) {
        fd.append("user_customization", personalizeText.trim());
      }
      // iter 2026-02 — Per singola foto in stage, se l'item ha un roomHint
      // per-tile diverso dal global room, prevale il per-tile.
      const perTileRoom = item.roomHint || room;
      if (perTileRoom) {
        // Sovrascrivi eventuale room_hint globale già aggiunto sopra
        fd.delete("room_hint");
        fd.append("room_hint", perTileRoom);
      }
    }
    if (mode === "restyle") {
      fd.append("change_floor", String(!!changeFloor));
      if (changeFloor) fd.append("floor_style", floorStyle);
    }
    // iter v28 r287 — Empty room: niente style/pavimento. Bypass 422
    // "stanza già vuota" se l'utente conferma di procedere comunque.
    if (mode === "empty_room") {
      if (opts.confirmNotFurnished) fd.append("confirm_not_furnished", "true");
    }
    // iter v30 r147 — Restyling esterno: nessuno style/room. Solo `compact`
    // (toggle "casa piccola" → no piscine inventate, giardino minimal).
    if (mode === "exterior_restyle") {
      fd.append("compact", String(!!compact));
    }
    if (mode === "enhance") {
      // iter v28 r358: opts.focusOverride consente al bottone "Ultra-fedele"
      // di forzare focus=fedele al volo senza aspettare il re-render dello
      // state (setState è async, useremmo il valore vecchio dentro la closure).
      fd.append("focus", opts.focusOverride || buildFocusParam());
    }
    // iter r97e · User M1520 — iOS backgrounding-safe pattern:
    //  1. Genero un `client_request_id` UUID unico per questa operazione
    //  2. Lo passo al backend come idempotency key
    //  3. Se durante l'attesa iOS sospende la WKWebView e la fetch muore,
    //     al ritorno in foreground il backend potrebbe aver salvato
    //     comunque l'artifact — chiamiamo `/live/recover/{id}` per
    //     recuperarlo senza far ripagare crediti all'utente.
    //  4. Registro l'op in localStorage così sopravvive anche a killing
    //     completo dell'app (visibilitychange handler la ripesca).
    const clientRequestId = newRequestId();
    fd.append("client_request_id", clientRequestId);
    trackPendingAiOp({
      requestId: clientRequestId,
      mode,
      itemId: item.id,
      propertyId: null, // FotoEditor è "live", non attaccato a un property_id
    });

    const endpoint =
      mode === "enhance" ? "/live/enhance" :
      mode === "restyle" ? "/live/restyle" :
      mode === "furnish_plan" ? "/live/furnish-plan" :
      mode === "empty_room" ? "/live/empty-room" :
      mode === "exterior_restyle" ? "/live/exterior-restyle" :
      "/live/stage";

    // iter v32 r37 — Su Capacitor iOS la WKWebView viene sospesa quando
    // l'utente manda l'app in background (lockscreen / altre app / home).
    // iter r97e — Ora recuperiamo l'artifact via `client_request_id` invece
    // di mostrare errore "Riprova". L'operazione backend gira comunque
    // fino in fondo (crediti già stati consumati) e il risultato viene
    // salvato in db.live_previews. Il client polla /live/recover al
    // foreground per riprendere lo stato.
    let wasInBackground = false;
    const onVisChange = () => {
      if (document.visibilityState === "hidden") wasInBackground = true;
    };
    document.addEventListener("visibilitychange", onVisChange);
    try {
      // WakeLock: mantiene lo schermo attivo → previene la maggior parte dei
      // casi di suspension (iOS 16.4+ supporta l'API standard).
      const data = await withWakeLock(async () => {
        const resp = await api.post(endpoint, fd, {
          headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
        });
        return resp.data;
      });
      clearPendingAiOp(clientRequestId);
      return data;
    } catch (err) {
      if (wasInBackground) {
        // Prima di mostrare errore, aspetto 3s e tento il recovery. Il backend
        // continua a girare in background pure con WebView sospesa; se ha
        // salvato l'artifact, il recovery lo restituisce.
        try { err._wasInBackground = true; } catch { /* */ }
        try {
          await new Promise((r) => setTimeout(r, 3000));
          const rec = await recoverArtifact(api, clientRequestId);
          if (rec?.found) {
            // Successo silenzioso: costruiamo la stessa shape dell'endpoint
            // originale così il caller non deve sapere che è recovery.
            clearPendingAiOp(clientRequestId);
            toast.success("Recuperata la generazione", {
              description: "L'app era in background: ho ripreso il risultato senza rifare l'operazione.",
              duration: 4500,
            });
            return {
              id: rec.id,
              before_path: rec.before_path,
              after_path: rec.after_path,
              kind: rec.kind || mode,
              style: rec.style,
              quota_used_after: 0,
              quota_cap: 0,
              recovered: true,
            };
          }
          // Recovery non ancora disponibile: potrebbe essere in corso.
          // Tentiamo un secondo poll dopo altri 8s (il grosso delle chiamate
          // Nano Banana finisce entro 30s).
          await new Promise((r) => setTimeout(r, 8000));
          const rec2 = await recoverArtifact(api, clientRequestId);
          if (rec2?.found) {
            clearPendingAiOp(clientRequestId);
            toast.success("Recuperata la generazione", {
              description: "L'app era in background: ho ripreso il risultato senza rifare l'operazione.",
              duration: 4500,
            });
            return {
              id: rec2.id,
              before_path: rec2.before_path,
              after_path: rec2.after_path,
              kind: rec2.kind || mode,
              style: rec2.style,
              quota_used_after: 0,
              quota_cap: 0,
              recovered: true,
            };
          }
        } catch { /* recovery failed, cade nel throw originale */ }
      }
      throw err;
    } finally {
      document.removeEventListener("visibilitychange", onVisChange);
    }
  };

  const runAi = async () => {
    // iter v33 r5 — User M1478: blocca azioni AI quando un sopralluogo è
    // in lavorazione (la pipeline kit_worker sta già usando Nano Banana,
    // lanciare un'altra coda significa rallentare entrambi).
    try {
      const flag = JSON.parse(localStorage.getItem("pronto_in_creazione_v1") || "null");
      if (flag?.pid) {
        toast.warning("Elaborazione in pausa", {
          description: "Sto preparando il tuo sopralluogo (~4-6 min). L'editor foto torna disponibile appena finisco.",
          duration: 5000,
        });
        return;
      }
    } catch { /* localStorage non disponibile, procedi */ }

    // iter v32 r12 — Guard sincrono anti-doppio-tap. Senza questo, click
    // rapidi (mobile soprattutto) potevano avviare 2-3 generazioni
    // sovrapposte → addebito multiplo crediti.
    if (submittingRef.current) return;
    if (!batchActive || anyProcessing) return;
    // iter v32 r37e — Dismetti il banner iOS-background al primo tap su
    // Genera. Lo persistiamo in localStorage perché serve solo "la prima
    // volta che l'utente prova a generare".
    if (iosBgBannerVisible) {
      try { localStorage.setItem("foto-editor:ios-bg-banner-seen", "1"); } catch { /* */ }
      setIosBgBannerVisible(false);
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await _runAiInner();
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  // iter 2026-02 — Multi-foto Arreda: chiamata unica al batch endpoint.
  // Riusa la reference chain server-side + refund automatico per foto
  // fallite. Mantiene la UX identica a quella singola (spinner, error,
  // download) senza doppia rete di reference client-side.
  const _runStageBatch = async (pending) => {
    const fd = new FormData();
    // Costruisci metadata + attacca i files
    const metadata = pending.map((it) => {
      const hint = it.roomHint || room || "";
      const meta = { room_hint: hint, client_request_id: newRequestId() };
      // iter 2026-02 — Se toggle "Stessa stanza" attivo, group_id = roomHint.
      // Foto Automatico (roomHint vuoto) restano senza gruppo.
      if (groupSameRoom && hint) {
        meta.group_id = `same-room:${hint}`;
      }
      return meta;
    });
    // Attach files nell'ordine di pending, matching metadata
    pending.forEach((it) => fd.append("files", it.file));
    fd.append("metadata_json", JSON.stringify(metadata));
    fd.append("style", resolveStyle(style));
    fd.append("change_floor", String(!!changeFloor));
    if (changeFloor) fd.append("floor_style", floorStyle);
    fd.append("compact", "false");
    if (personalizeText.trim()) fd.append("user_customization", personalizeText.trim());

    // Traccia gli id come pending client-side (metadata client_request_id
    // permette il recovery iOS-background).
    metadata.forEach((m, i) => {
      trackPendingAiOp({
        requestId: m.client_request_id,
        mode: "stage",
        itemId: pending[i].id,
        propertyId: null,
      });
    });

    // Marca tutti come processing
    setItems((prev) => prev.map((x) => {
      const idx = pending.findIndex((p) => p.id === x.id);
      return idx >= 0 ? { ...x, status: "processing", error: null } : x;
    }));

    const _bgHandle = bgJobs.start("photo_stage");
    try {
      const { data } = await api.post("/live/stage-batch", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 900_000, // 15 min per grandi batch
      });
      _bgHandle.end(true);

      // Mappa risultati per index → itemId
      const results = data?.results || [];
      let okCount = 0;
      let failCount = 0;
      setItems((prev) => prev.map((x) => {
        const idx = pending.findIndex((p) => p.id === x.id);
        if (idx < 0) return x;
        const r = results[idx];
        if (!r) return { ...x, status: "error", error: "Nessuna risposta" };
        if (r.ok) {
          okCount += 1;
          return {
            ...x,
            status: "done",
            result: {
              id: r.id,
              before_path: r.before_path,
              after_path: r.after_path,
              kind: r.kind,
              style: r.style,
              quota_used_after: 0,
              quota_cap: -1,
            },
          };
        }
        failCount += 1;
        return { ...x, status: "error", error: r.error || "Generazione fallita" };
      }));

      metadata.forEach((m) => clearPendingAiOp(m.client_request_id));

      if (data?.refunded_credits > 0) {
        toast.info(
          `Rimborsati ${data.refunded_credits} crediti · ${data.failed} foto non riuscite`,
          { duration: 5000 },
        );
      }
      if (okCount > 0 && failCount === 0) {
        toast.success(`${okCount} foto arredate ✓`, { duration: 3500 });
      } else if (okCount > 0 && failCount > 0) {
        toast.warning(`${okCount} riuscite · ${failCount} da riprovare`, { duration: 5000 });
      } else if (failCount > 0) {
        toast.error("Nessuna foto arredata · crediti rimborsati", { duration: 5000 });
      }
    } catch (err) {
      _bgHandle.end(false, err?.message || "Errore batch");
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg = (typeof detail === "string" ? detail : detail?.message) || "Batch fallito";
      if (status === 402) {
        toast.error("Crediti insufficienti", { description: msg, duration: 6000 });
      } else {
        toast.error("Errore durante l'elaborazione batch", { description: msg, duration: 6000 });
      }
      setItems((prev) => prev.map((x) => {
        const idx = pending.findIndex((p) => p.id === x.id);
        return idx >= 0 ? { ...x, status: "error", error: msg } : x;
      }));
    }
  };

  const _runAiInner = async () => {
    // iter 275: rispetta selezione. Process solo gli item NON esclusi.
    const toProcess = items.filter((it) => !excludedIds.has(it.id));
    if (toProcess.length === 0) {
      toast.error("Seleziona almeno una foto da generare");
      return;
    }

    // iter v28 r468 — User M1393: «se uno carica tante foto non si impalli».
    // Pre-check crediti: se l'utente ha meno crediti delle foto selezionate,
    // mostra un avviso PRIMA di partire invece di processare metà batch
    // e poi rimbalzare su HTTP 402. Best-effort: se `/api/credits/me` non
    // risponde o lo stato dei crediti è "Illimitato" → procedi normale.
    try {
      const { data: q } = await api.get("/credits/me");
      const remaining = q?.balance_total ?? null;
      const need = toProcess.filter((it) => it.status !== "done").length;
      if (typeof remaining === "number" && remaining < need && remaining < 100) {
        // 100 = soglia "illimitato pratico" (pro/legacy)
        const ok = window.confirm(
          `Hai ${remaining} cred${remaining === 1 ? "ito" : "iti"} disponibil${remaining === 1 ? "e" : "i"}.\n` +
          `Stai per processare ${need} foto.\n\n` +
          `Le prime ${remaining} saranno generate, le restanti ${need - remaining} si fermeranno.\n\n` +
          `Vuoi procedere comunque?`,
        );
        if (!ok) return;
      }
    } catch { /* network err → procedi normalmente */ }

    // iter 2026-02 — Multi-foto Arreda: se mode=stage e ho ≥2 foto pending,
    // uso l'endpoint batch /live/stage-batch invece del loop client-side.
    // Vantaggio: 1 sola request, gestione reference chain + refund lato server.
    const pendingStage = toProcess.filter((it) => it.status !== "done");
    if (mode === "stage" && pendingStage.length >= 2) {
      await _runStageBatch(pendingStage);
      return;
    }

    // iter 275 round 47: counter locali (non dallo state, che è stale dentro
    // la closure). Tracking puntuale dei success / fail e differenziazione
    // del toast finale.
    let okCount = 0;
    let failCount = 0;
    let fatalAbort = false;
    let fatalMsg = "";
    // Process sequentially (Nano Banana doesn't like parallel calls; also keeps quota predictable)
    // iter v28 r447 — User M1360: coerenza multi-foto. Quando processo
    // più foto in modalità stage, dopo la prima salvo il path della staged
    // e lo passo come `styleReferenceUrl` alle successive. Risultato: tutte
    // le foto dello stesso ambiente condividono la stessa palette/atmosfera/
    // stile arredo (NON gli stessi mobili identici, è impossibile).
    let sessionStyleRef = null;
    for (const it of toProcess) {
      if (it.status === "done") {
        okCount += 1;
        // Recupera il path della prima foto già fatta in sessione
        // (caso: l'utente rilancia il batch dopo aver già processato qualcosa).
        if (!sessionStyleRef && mode === "stage" && it.result?.after_path) {
          sessionStyleRef = it.result.after_path;
        }
        continue;
      }
      if (fatalAbort) {
        // iter 275 round 47: errori fatali (crediti esauriti / rate limit /
        // service unavailable) si applicano a tutto il batch → marchiamo
        // i restanti come "in attesa", l'utente li rilancerà dopo aver
        // risolto la causa. Non spammiamo l'API.
        setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, status: "idle", error: fatalMsg } : x));
        continue;
      }
      setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, status: "processing", error: null } : x));
      // iter v31 r13 — Registra il job nel tray globale così l'agente vede
      // lo stato anche fuori dalla pagina (cambio sezione, navigazione).
      const _bgHandle = bgJobs.start(bgKindForMode(mode));
      try {
        // iter v28 r447 — passa il reference della 1ª staged in modalità stage
        const runOpts = sessionStyleRef && mode === "stage"
          ? { styleReferenceUrl: sessionStyleRef }
          : {};
        const data = await runOne(it, runOpts);
        _bgHandle.end(true);
        setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, status: "done", result: data } : x));
        // Salva la 1ª staged completata come reference per le successive
        // (`after_path` è il path object-storage che il backend sa risolvere)
        if (!sessionStyleRef && mode === "stage" && data?.after_path) {
          sessionStyleRef = data.after_path;
        }
        setQuota((q) => q ? {
          ...q,
          used: data.quota_cap > 0 ? data.quota_used_after : q.used,
          remaining: q.cap > 0 ? Math.max(0, q.cap - data.quota_used_after) : -1,
        } : q);
        okCount += 1;
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        // iter v32 r37 — Se l'app è stata in background durante la chiamata
        // (WKWebView sospesa su iOS), mostra messaggio onesto invece del
        // generico "Generazione non riuscita".
        const inBg = !!err?._wasInBackground;
        const msg = inBg
          ? "L'app è stata in background — premi Rigenera per riprovare"
          : ((typeof detail === "string" ? detail : detail?.message) || "Generazione non riuscita");
        _bgHandle.end(false, msg);
        setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, status: "error", error: msg } : x));
        failCount += 1;
        if (inBg) {
          toast.warning("Pronto si era messo in pausa", {
            description: "iOS sospende l'app quando passi ad altre. Premi Rigenera, questa volta resta in primo piano.",
            duration: 8000,
          });
        }
        // iter v28 r93 — Stanza già arredata in mode=stage: 422 → mostra
        // toast con azione "Procedi comunque" che retry-a la stessa foto con
        // confirm_not_empty=true (servirà a runOne; vedi sotto).
        if (status === 422 && mode === "stage") {
          toast.warning("Stanza già arredata", {
            description: msg,
            duration: 9000,
            action: {
              label: "Procedi comunque",
              onClick: async () => {
                setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, status: "processing", error: null } : x));
                try {
                  const data = await runOne(it, { confirmNotEmpty: true });
                  setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, status: "done", result: data } : x));
                } catch (e2) {
                  const m2 = e2?.response?.data?.detail || "Generazione non riuscita";
                  setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, status: "error", error: typeof m2 === "string" ? m2 : "Generazione non riuscita" } : x));
                }
              },
            },
          });
        }
        // iter v28 r287 — Speculare per "Svuota stanza": 422 = stanza già
        // vuota → bottone "Procedi comunque" che passa confirm_not_furnished.
        if (status === 422 && mode === "empty_room") {
          toast.warning("Stanza già vuota", {
            description: msg,
            duration: 9000,
            action: {
              label: "Procedi comunque",
              onClick: async () => {
                setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, status: "processing", error: null } : x));
                try {
                  const data = await runOne(it, { confirmNotFurnished: true });
                  setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, status: "done", result: data } : x));
                } catch (e2) {
                  const m2 = e2?.response?.data?.detail || "Generazione non riuscita";
                  setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, status: "error", error: typeof m2 === "string" ? m2 : "Generazione non riuscita" } : x));
                }
              },
            },
          });
        }
        // Errori "fatali" che si ripeterebbero su ogni foto → fermati subito.
        if (status === 402 || status === 429 || status === 503) {
          fatalAbort = true;
          fatalMsg = msg;
        }
      }
    }
    // Refresh history in background (gallery API → consistent schema with `url`)
    api.get("/gallery?limit=100").then(({ data: h }) => {
      const list = h || [];
      setHistory(list);
      try { localStorage.setItem("pronto:cache:gallery", JSON.stringify(list)); } catch { /* quota full */ }
    }).catch(() => {});

    // iter 275 round 47: toast differenziato.
    const total = toProcess.length;
    if (fatalAbort) {
      // Crediti / rate limit / down service: messaggio chiaro
      toast.error(`${okCount}/${total} pronte. ${fatalMsg}`, { duration: 4500 });
    } else if (failCount === 0) {
      // iter v28 r357 — User M1267: «in ogni foto modificata, in ogni arredo
      // nuovo apri un popup "Non ti piace? Rigenera"». Toast persistente
      // (8s) con action button che rilancia la generazione sull'unica foto
      // appena fatta (o sulla prima se sono multiple).
      const lastId = toProcess[toProcess.length - 1]?.id;
      toast(`${okCount} ${okCount === 1 ? "foto pronta" : "foto pronte"} · non ti piace?`, {
        description: okCount === 1
          ? "Pronto sa che a volte il primo risultato non è perfetto."
          : "Ne vuoi rifare una? Clicca Rigenera sotto la foto.",
        duration: 9000,
        action: lastId && okCount === 1 ? {
          label: "Rigenera",
          onClick: () => regenerateOne(lastId),
        } : undefined,
      });
    } else if (okCount === 0) {
      toast.error(`Generazione fallita. ${failCount === 1 ? "Riprova" : `Riprova le ${failCount} foto`}.`, { duration: 4000 });
    } else {
      toast.message(`${okCount}/${total} pronte · ${failCount} da riprovare`, { duration: 3500 });
    }
  };

  const removeItem = (id) => {
    setItems((prev) => {
      const it = prev.find((x) => x.id === id);
      if (it?.previewUrl) URL.revokeObjectURL(it.previewUrl);
      return prev.filter((x) => x.id !== id);
    });
    // iter 275: pulisci anche dalla lista esclusi
    setExcludedIds((prev) => {
      if (!prev.has(id)) return prev;
      const n = new Set(prev);
      n.delete(id);
      return n;
    });
  };

  // Rerun AI on a specific item (used by "Rigenera" button). Does not touch other items.
  // iter v28 r358: opts.focusOverride consente di forzare un focus diverso
  // (es. Ultra-fedele) senza modificare lo state principale.
  const regenerateOne = async (id, opts = {}) => {
    const it = items.find((x) => x.id === id);
    if (!it) return;
    setItems((prev) => prev.map((x) => x.id === id ? { ...x, status: "processing", error: null } : x));
    // iter v31 r13 — Tray globale: rigenerazione di una singola foto.
    const _bgHandle = bgJobs.start(bgKindForMode(mode));
    try {
      const data = await runOne(it, opts);
      _bgHandle.end(true);
      setItems((prev) => prev.map((x) => x.id === id ? { ...x, status: "done", result: data } : x));
      setQuota((q) => q ? {
        ...q,
        used: data.quota_cap > 0 ? data.quota_used_after : q.used,
        remaining: q.cap > 0 ? Math.max(0, q.cap - data.quota_used_after) : -1,
      } : q);
      api.get("/gallery?limit=100").then(({ data: h }) => {
        const list = h || [];
        setHistory(list);
        try { localStorage.setItem("pronto:cache:gallery", JSON.stringify(list)); } catch { /* quota full */ }
      }).catch(() => {});
    } catch (err) {
      // iter 275 round 47: estrai messaggio robusto. Detail può essere
      // stringa, oggetto {message}, o assente. Senza handling, l'oggetto
      // finiva in toast come "[object Object]".
      const detail = err?.response?.data?.detail;
      // iter v32 r37 — Background suspend: messaggio onesto.
      const inBg = !!err?._wasInBackground;
      const msg = inBg
        ? "L'app è stata in background — premi Rigenera per riprovare"
        : ((typeof detail === "string" ? detail : detail?.message) || "Generazione non riuscita");
      _bgHandle.end(false, msg);
      setItems((prev) => prev.map((x) => x.id === id ? { ...x, status: "error", error: msg } : x));
      if (inBg) {
        toast.warning("Pronto si era messo in pausa", {
          description: "iOS sospende l'app quando passi ad altre. Premi Rigenera, questa volta resta in primo piano.",
          duration: 8000,
        });
      } else {
        toast.error(msg);
      }
    }
  };

  const reset = () => {
    items.forEach((it) => { if (it.previewUrl) URL.revokeObjectURL(it.previewUrl); });
    setItems([]);
  };

  // iter v28 r85 — Cambio modalità: mantieni i file caricati, azzera SOLO
  // status/result/error. Prima il cambio modalità chiamava reset() →
  // l'utente perdeva la foto caricata (user feedback: "se uno carica una
  // foto nell'editor e cambia cosa vuol fare la foto si perde").
  const softResetForModeChange = () => {
    setItems((prev) => prev.map((it) => ({
      ...it,
      status: "pending",
      result: null,
      error: null,
    })));
  };

  const changeMode = (newMode) => {
    if (mode !== newMode) {
      setMode(newMode);
      softResetForModeChange();
    }
    // iter v28 r449 — User M1363: «schiacci "Parti da qui · Stanza vuota" e
    // non vedi che sotto cambia». Fix: scroll smooth verso l'uploader così
    // l'agente vede SUBITO il caricamento foto con il testo aggiornato.
    // iter v29 r214 — User M1504 ribadito: «se clicco non mi porta giù».
    // iter v29 r221 — User M1547: anche cliccando la SAME mode il porta
    // giù è atteso (l'utente vuole comunque vedere l'uploader). Quindi
    // scrolla sempre, anche se la mode è già selezionata.
    setTimeout(() => {
      const node = uploaderRef.current || document.getElementById("foto-uploader");
      if (node && typeof node.scrollIntoView === "function") {
        node.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 160);
  };

  const shareWhatsapp = async () => {
    const r = singleResultItem?.result;
    if (!r) return;
    // iter v28 r356 — User feedback: «non mandare "Foto migliorata con Pronto"
    // nei messaggi WhatsApp». Lasciamo title vuoto (alcuni share sheet lo
    // ignorano comunque).
    // iter v30 r095 — User M1750: testo neutro "Ecco la foto" come etichetta
    // del file (capire COSA arriva su WhatsApp). Nessun claim commerciale.
    await shareImage({
      url: liveFileUrl(r.id, "after"),
      filename: `pronto-after-${r.id.slice(0, 8)}.jpg`,
      title: "",
      text: "Ecco la foto",
    });
  };

  const shareBeforeAfter = async () => {
    const r = singleResultItem?.result;
    if (!r) return;
    await shareImage({
      url: liveBeforeAfterCardUrl(r.id),
      filename: `pronto-prima-dopo-${r.id.slice(0, 8)}.png`,
      title: "",
      text: "Ecco la card prima e dopo",
    });
  };

  const shareBeforeAfterVideo = async () => {
    const r = singleResultItem?.result;
    if (!r) return;
    // iter v31 r10 — La toast "Preparo il video 6s…" deve SEMPRE essere
    // dismissed alla fine, sia successo che fallimento, altrimenti resta
    // appiccicata sopra altri toast di errore creando confusione. Il toast
    // di errore vero arriva da shareVideo() (gestisce internamente download
    // failed / server_busy / timeout).
    toast.loading("Preparo il video 8s…", { id: "va-video" });
    // iter v31 r13 — Tray globale: video Prima/Dopo in lavorazione (~10-20s).
    const _bgHandle = bgJobs.start("video_before_after");
    let _ok = true;
    try {
      await shareVideo({
        url: liveBeforeAfterVideoUrl(r.id),
        filename: `pronto-prima-dopo-${r.id.slice(0, 8)}.mp4`,
        title: "",
        text: "Ecco il video prima e dopo",
      });
    } catch (e) {
      _ok = false;
      _bgHandle.end(false, e?.message || "Errore video");
      throw e;
    } finally {
      toast.dismiss("va-video");
      if (_ok) _bgHandle.end(true);
    }
  };

  const downloadOne = async (resultId, variant = "after") => {
    if (!resultId) return;
    // iter v28 r459 — User M1376: «se clicco Salva deve solo salvare, non
    // deve aprire il pannello Condividi, soprattutto da desktop». Rimosso
    // tutto il branch `navigator.share()` da questa funzione. Save = solo
    // download. Lo share sheet resta nel pulsante "Condividi" dedicato.
    //
    // iter v28 r459 — Aggiunto `variant` (after/before) per scaricare anche
    // l'originale.
    //
    // iter v28 r472 — User M1382: «Salva in galleria su mobile non fa nulla».
    // Su iOS l'unico modo per finire DAVVERO in Galleria Foto è il share
    // sheet nativo. Quindi: device-aware (mobile → shareImage, desktop →
    // download diretto). Su mobile l'utente fa "Salva immagine" dallo sheet.
    const url = liveFileUrl(resultId, variant);
    const suffix = variant === "before" ? "originale" : "modificata";
    const filename = `pronto-foto-${suffix}-${resultId.slice(0, 8)}.jpg`;

    const isMobile = typeof navigator !== "undefined" && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
    if (isMobile) {
      await shareImage({ url, filename, title: "", text: "" });
      return;
    }

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      if (blob.size === 0 || blob.type.startsWith("text/")) {
        throw new Error("Empty/invalid blob");
      }
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
      toast.success(variant === "before" ? "Foto originale scaricata" : "Foto scaricata");
    } catch (err) {
      // Fallback estremo: link diretto (utile su Safari con CORS)
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Foto aperta — usa 'Salva con nome'");
    }
  };

  const downloadAll = async () => {
    const done = items.filter((x) => x.status === "done" && x.result);
    for (const it of done) await downloadOne(it.result.id);
  };

  // Lightbox galleria — apri foto in grande con azioni (rimodifica, scarica, condividi)
  const [lightboxItem, setLightboxItem] = useState(null);
  // iter v26 r10 — Multi-selezione nella galleria sotto l'editor:
  // selectMode + set di id selezionati. Stessa UX di GalleriaFoto:
  //   • bottone "Seleziona" in toolbar
  //   • check circle visibile solo in selectMode
  //   • sticky bar in basso con azioni (Riprova / Scarica ZIP / Cancella)
  //   • validation: Riprova rifiuta mix foto+pianta (`furnish_plan` vs altri)
  const [historySelectMode, setHistorySelectMode] = useState(false);
  const [historySelected, setHistorySelected] = useState({});
  const historySelectedIds = React.useMemo(
    () => Object.keys(historySelected).filter((k) => historySelected[k]),
    [historySelected],
  );
  const historySelectedCount = historySelectedIds.length;
  const toggleHistorySelected = (id) =>
    setHistorySelected((s) => ({ ...s, [id]: !s[id] }));  // iter 275 round 19: toggle Prima/Dopo dentro il lightbox (solo per le foto
  // "live" migliorate/arredate). Default: dopo. Tap su un bottone toggla.
  const [lightboxView, setLightboxView] = useState("after"); // "before" | "after"
  // Menu kebab per tile (Prima/Dopo · Salva · Elimina). Tiene l'id della
  // tile su cui è aperto. Null = chiuso.
  const [tileMenuOpenId, setTileMenuOpenId] = useState(null);
  // Modale conferma cancellazione di una foto della history (live items)
  const [historyDeleteConfirm, setHistoryDeleteConfirm] = useState(null); // {id, label}
  // Chiudi menu su click ovunque
  useEffect(() => {
    if (!tileMenuOpenId) return;
    const onClick = () => setTileMenuOpenId(null);
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [tileMenuOpenId]);
  // Chiudi con Esc
  useEffect(() => {
    if (!lightboxItem) return;
    const onKey = (e) => { if (e.key === "Escape") setLightboxItem(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxItem]);
  // Reset toggle quando cambia foto
  useEffect(() => { setLightboxView("after"); }, [lightboxItem?.id]);

  // iter 275 round 19: cancella foto della history (live items o property
  // photo). Usa lo stesso endpoint della Galleria.
  const _doDeleteHistory = async (rawId) => {
    if (!rawId) return;
    try {
      const realId = rawId.includes(":") ? rawId.split(":", 2)[1] : rawId;
      const isLive = rawId.startsWith("live:") || !rawId.startsWith("prop:");
      if (isLive) await api.delete(`/live/${realId}`);
      else await api.delete(`/photos/${realId}`);
      setHistory((prev) => prev.filter((x) => x.id !== rawId));
      if (lightboxItem?.id === rawId) setLightboxItem(null);
      toast.success("Foto cancellata");
    } catch (err) {
      toast.error("Errore cancellazione");
    }
  };

  // "Rimodifica": prende la foto dalla galleria, la trasforma in File e la
  // carica nell'editor come fosse un nuovo upload, così l'agente può
  // sceglierne una nuova modalità (es. enhance → restyle, o cambiare focus).
  //
  // iter 305 round 8: BUG FIX — "Rifai con altre impostazioni" deve SEMPRE
  // ripartire dall'immagine ORIGINALE (before), non dal risultato precedente
  // (after). Altrimenti l'utente lavora su un'immagine già rielaborata e
  // i nuovi tentativi si "sporcano" stratificandosi.
  //
  // iter v26 r5 — Estesa la fix anche alle foto delle property: il backend
  // ora espone `original_url` su /api/gallery sia per i live che per i
  // property items. Prima della fix, per source=property usavamo
  // `galleryItemUrl(h)` che punta a enhanced_path/staged_path → AI lavorava
  // sulla foto GIÀ lavorata. Ora preferiamo sempre `original_url` se presente.
  const reopenInEditor = async (h) => {
    const isLive = h?.source === "live";
    const realId = (h?.id || "").replace(/^(live|prop):/, "");
    // iter v26 r7 — Auto-detect MODE in base al kind della foto.
    // Bug user-reported: "Se è una pianta deve andare in 'Arreda pianta',
    // se è una foto deve andare nelle altre 3 e non in 'Arreda pianta'".
    // Prima il mode dell'editor restava quello attivo → AI lavorava la foto
    // come se fosse una pianta (o viceversa).
    const VALID_MODES = ["enhance", "stage", "restyle", "furnish_plan"];
    const detectedMode = VALID_MODES.includes(h?.kind) ? h.kind : "enhance";
    if (detectedMode !== mode) {
      setMode(detectedMode);
    }
    // Preferenza: original_url dal backend (nativo) > liveFileUrl before
    // (legacy live) > galleryItemUrl (fallback estremo, foto già lavorata)
    let src;
    if (h?.original_url) {
      const token = Cookies.get("vista_jwt");
      const sep = h.original_url.includes("?") ? "&" : "?";
      const tokenParam = token ? `${sep}auth=${token}` : "";
      const cleanPath = h.original_url.startsWith("/api/") ? h.original_url.slice(4) : h.original_url;
      src = `${API}${cleanPath}${tokenParam}`;
    } else if (isLive) {
      src = liveFileUrl(realId, "before");
    } else {
      src = galleryItemUrl(h);
    }
    try {
      toast.loading("Apro l'editor…", { id: "rimodifica" });
      const res = await fetch(src);
      const blob = await res.blob();
      // Reset eventuali items in coda + carica solo questo
      items.forEach((it) => { if (it.previewUrl) URL.revokeObjectURL(it.previewUrl); });
      const file = new File([blob], `pronto-foto-${(h.id || "").slice(0, 10)}.jpg`, { type: blob.type || "image/jpeg" });
      const previewUrl = URL.createObjectURL(blob);
      setItems([{
        id: `${Date.now()}-rimodifica`,
        file,
        previewUrl,
        status: "pending",
        result: null,
        error: null,
      }]);
      setLightboxItem(null);
      toast.success("Pronto per riprovare", { id: "rimodifica", duration: 1800 });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Impossibile aprire la foto", { id: "rimodifica" });
    }
  };

  // iter v28 r363 — User M1273 (mobile bug): «riapro le guide ma non le vedo».
  // Bug originale: le chiavi resetate erano OBSOLETE (`fotoeditor:intro` e
  // `fotoeditor:4-modi`) mentre la guida reale usa `fotoeditor:5-modi`.
  // Eliminata la funzione locale + sostituita col componente standard
  // ReopenGuidesBtn (single source of truth → niente più chiavi disallineate).

  return (
    <AppShell title="Editor foto" breadcrumb="Editor foto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/dashboard")}
          className="text-sm text-[#6B6A65] hover:text-[#1C1C1A] inline-flex items-center gap-1.5 mb-3"
          data-testid="foto-back-btn"
        >
          <ArrowLeft size={14} weight="bold" /> Dashboard
        </button>
        <div className="flex items-center justify-between gap-3 mb-2">
          <h1 className="text-2xl font-semibold text-[#1C1C1A] tracking-tight">
            Editor foto
          </h1>
          <div className="flex items-center gap-2">
            <ReopenGuidesBtn
              keys={["fotoeditor:5-modi"]}
              testId="foto-reopen-guides"
            />
            {isPro ? (
              <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-white bg-[#7C8B6E] px-2 py-1 rounded">Pro · 320 crediti / mese</span>
            ) : quota ? (
              <span className="text-[11.5px] text-[#6B6A65]" data-testid="foto-quota">
                {/* iter v32 r37 — cap = -1 significa illimitato (whitelist/legacy).
                    Mostrare "-1/-1 oggi" letteralmente è un bug visivo. */}
                {quota.cap < 0 || quota.unlimited
                  ? "Illimitato oggi"
                  : `${quota.remaining}/${quota.cap} oggi`}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* iter v25 — Unificato: due box "Carica le tue foto..." + "Cosa puoi fare"
          erano confusi (2 X da chiudere). Ora un solo onboarding dismissable
          con il sub-copy che spiega anche "carica · scegli · scarica/condividi". */}
      <SectionOnboarding
        storageKey="fotoeditor:5-modi"
        overline="Cosa puoi fare con l'Editor"
        title={<>Carica le foto · scegli lo <span className="italic-accent text-[#7C8B6E]">strumento giusto</span> · scarica o condividi</>}
        subtitle="Ogni foto può essere lavorata in modo diverso. Tocca una card per partire dallo strumento giusto."
        steps={[
          {
            key: "enhance",
            Icon: Lightning,
            overline: "1 · Migliora",
            title: "Foto già scattate",
            body: "Luce, colori, contrasto e ordine. Stessi mobili, stesso spazio.",
            accent: "#7C8B6E",
            onClick: () => changeMode("enhance"),
          },
          {
            key: "stage",
            Icon: Armchair,
            overline: "2 · Arreda",
            title: "Spazio vuoto o cantiere",
            body: "Lo spazio è vuoto? Arredalo in stile (moderno, scandinavo…).",
            accent: "#C49A2A",
            onClick: () => changeMode("stage"),
          },
          {
            key: "empty_room",
            Icon: Broom,
            overline: "3 · Svuota",
            title: "Spazio arredato",
            body: "Mostra al compratore la metratura reale, senza i mobili attuali.",
            accent: "#6B8E7F",
            onClick: () => changeMode("empty_room"),
          },
          {
            key: "restyle",
            Icon: Sparkle,
            overline: "4 · Rinnova",
            title: "Arredo datato",
            body: "Stesso spazio, mobili nuovi. Per case da ristrutturare.",
            accent: "#8B5E7C",
            onClick: () => changeMode("restyle"),
          },
          {
            key: "furnish_plan",
            Icon: MapTrifold,
            overline: "5 · Pianta",
            title: "Planimetria catastale",
            body: "Pianta 2D → mobili disegnati nelle stanze. Vista dall'alto.",
            accent: "#4A6F8B",
            onClick: () => changeMode("furnish_plan"),
          },
          {
            // iter v30 r147 — Restyling esterno (User: «villa abbandonata → ristrutturata»)
            key: "exterior_restyle",
            Icon: HouseLine,
            overline: "6 · Restyling esterno",
            title: "Casa abbandonata",
            body: "Mostra la casa come fosse appena ristrutturata. Stile architettonico intatto.",
            accent: "#6E8B5E",
            onClick: () => changeMode("exterior_restyle"),
          },
        ]}
      />

      {/* iter 275: mode tabs più compatti — l'utente lamentava che erano
          enormi. Da `py-3` flex-col (icona sopra titolo) a `py-2.5` flex-row
          inline (icona accanto). */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1.5 mb-2.5" role="tablist" data-testid="foto-mode-tabs">
        {MODES.map((m) => {
          const Icon = m.icon;
          const active = mode === m.v;
          return (
            <button
              key={m.v}
              onClick={() => changeMode(m.v)}
              className={`py-2 px-2.5 text-[11.5px] font-medium rounded-lg inline-flex items-center justify-center gap-1.5 transition-all ${active ? "bg-[#1C1C1A] text-[#FFFFFF]" : "bg-[#F1F4ED] text-[#1C1C1A] hover:bg-[#E5E7E0]"}`}
              data-testid={`foto-mode-${m.v}`}
            >
              <Icon size={13} weight={active ? "fill" : "regular"} />
              <span className="truncate">{m.l}</span>
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-[#9A968B] mb-4 px-1" data-testid="foto-mode-desc">
        {currentMode?.desc}
      </p>

      {/* Style picker — shown for stage / restyle / furnish_plan */}
      {!allDone && (mode === "stage" || mode === "restyle") && (
        <div className="mb-5 space-y-4" data-testid="foto-style-row">
          {/* iter 2026-02 — Label rinominata "Diventerà..." → più diretta.
              Nascondi il global room-picker in modalità stage con 2+ foto
              (in quel caso ogni foto ha il proprio chip inline). */}
          {!(mode === "stage" && items.length >= 2) && (
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#6B6A65] mb-2">
              {mode === "stage" ? "Che ambiente vuoi creare?" : "Diventerà..."}
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 sm:flex-wrap sm:overflow-visible">
              {ROOM_HINTS.map((r) => (
                <button
                  key={r.v || "auto"}
                  onClick={() => setRoom(r.v)}
                  className={`shrink-0 px-3 py-1.5 text-xs rounded-full border transition-all ${room === r.v ? "bg-[#7C8B6E] text-[#FFFFFF] border-[#7C8B6E]" : "bg-[#FFFFFF] text-[#1C1C1A] border-[#E5E7E0] hover:border-[#7C8B6E]"}`}
                  data-testid={`foto-room-${r.v || "auto"}`}
                >
                  {r.l}
                </button>
              ))}
            </div>
          </div>
          )}
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#6B6A65] mb-2">
              {mode === "stage" ? "Scegli lo stile" : "Stile arredo"}
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 sm:flex-wrap sm:overflow-visible">
              {STAGE_STYLES.map((s) => (
                <button
                  key={s.v}
                  onClick={() => setStyle(s.v)}
                  className={`shrink-0 px-3.5 py-1.5 text-xs rounded-full border transition-all ${style === s.v ? "bg-[#1C1C1A] text-[#FFFFFF] border-[#1C1C1A]" : "bg-[#FFFFFF] text-[#1C1C1A] border-[#E5E7E0] hover:border-[#7C8B6E]"}`}
                  data-testid={`foto-style-${s.v}`}
                >
                  {s.l}
                </button>
              ))}
            </div>
          </div>
          {/* iter 2026-02 — Personalizza (opzionale): collapse minimal, resta
              chiuso di default. L'utente scrive direzione d'arredo aggiuntiva
              (es. "letto 120cm, colori chiari, scrivania"). Additive alle
              regole del prompt — le REGOLE ASSOLUTE prevalgono in conflitto. */}
          {mode === "stage" && (
            <div data-testid="foto-personalize-block">
              <button
                type="button"
                onClick={() => setPersonalizeOpen((v) => !v)}
                className="text-xs text-[#6B6A65] hover:text-[#1C1C1A] inline-flex items-center gap-1.5 transition-colors"
                data-testid="foto-personalize-toggle"
                aria-expanded={personalizeOpen}
              >
                <PencilSimple size={12} weight={personalizeOpen ? "fill" : "regular"} />
                Personalizza (opzionale)
                {personalizeText.trim() && !personalizeOpen && (
                  <span className="text-[10px] uppercase tracking-[0.12em] text-[#7C8B6E] font-semibold">· attivo</span>
                )}
              </button>
              {personalizeOpen && (
                <div className="mt-2">
                  <textarea
                    value={personalizeText}
                    onChange={(e) => setPersonalizeText(e.target.value.slice(0, 300))}
                    placeholder="Es. camera singola, letto 120 cm, scrivania, colori chiari"
                    className="w-full text-[13px] bg-white border border-[#E5E7E0] focus:border-[#7C8B6E] rounded-lg px-3 py-2 outline-none resize-none transition-colors"
                    rows={2}
                    maxLength={300}
                    data-testid="foto-personalize-input"
                  />
                  <div className="text-[10.5px] text-[#9A968B] mt-1 flex items-center justify-between">
                    <span>Note aggiuntive · le regole di arredo esistenti restano attive.</span>
                    <span>{personalizeText.length}/300</span>
                  </div>
                </div>
              )}
            </div>
          )}
          {(mode === "restyle" || mode === "stage") && (
            <div>
              <label className="flex items-center gap-2 cursor-pointer text-[12.5px] text-[#1C1C1A]">
                <input type="checkbox" checked={changeFloor} onChange={(e) => setChangeFloor(e.target.checked)} className="accent-[#7C8B6E]" data-testid="foto-floor-toggle" />
                <span>Cambia anche il <strong>pavimento</strong> <span className="text-[10.5px] text-[#9A968B]">(altrimenti mantiene l'originale)</span></span>
              </label>
              {changeFloor && (
                <div className="mt-2 ml-6 flex gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible">
                  {[
                    { v: "parquet_chiaro", l: "Parquet chiaro" },
                    { v: "parquet_scuro", l: "Parquet scuro" },
                    { v: "gres_pietra", l: "Gres effetto pietra" },
                    { v: "resina_chiara", l: "Resina chiara" },
                    { v: "marmo", l: "Marmo" },           /* iter v28 r39: era "Cemento lucido" */
                  ].map((f) => (
                    <button
                      key={f.v} onClick={() => setFloorStyle(f.v)}
                      className={`shrink-0 px-3 py-1.5 text-[11px] rounded-full border transition-all ${floorStyle === f.v ? "bg-[#7C8B6E] text-white border-[#7C8B6E]" : "bg-white text-[#1C1C1A] border-[#E5E7E0] hover:border-[#7C8B6E]"}`}
                      data-testid={`foto-floor-${f.v}`}
                    >
                      {f.l}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Floor plan — single option: a small "stile rendering" hint */}
      {!allDone && mode === "furnish_plan" && (
        <div className="mb-5" data-testid="foto-plan-hint">
          <div className="bg-[#F5F4F0] border border-[#E8E5DE] rounded-md p-3 text-[12.5px] text-[#3F3D38] leading-[1.55]">
            <strong className="text-[#1C1C1A]">Cerca di caricare una pianta di buona qualità.</strong> Pronto legge le scritte (Soggiorno, Camera, Bagno…) e arreda ogni stanza. <strong className="text-[#7C8B6E]">Sempre vista dall'alto.</strong>
          </div>
          <div className="mt-3">
            <div className="text-xs uppercase tracking-[0.18em] text-[#6B6A65] mb-2">Stile</div>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 sm:flex-wrap sm:overflow-visible">
              {STAGE_STYLES.map((s) => (
                <button
                  key={s.v}
                  onClick={() => setStyle(s.v)}
                  className={`shrink-0 px-3.5 py-1.5 text-xs rounded-full border transition-all ${style === s.v ? "bg-[#1C1C1A] text-[#FFFFFF] border-[#1C1C1A]" : "bg-[#FFFFFF] text-[#1C1C1A] border-[#E5E7E0] hover:border-[#7C8B6E]"}`}
                  data-testid={`foto-plan-style-${s.v}`}
                >
                  {s.l}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* iter v28 r287 — Svuota stanza: niente style/pavimento, solo hint.
          iter v28 r289 — Rimossa anche la sezione "Tipologia (opzionale)":
          non serve per svuotare, è ridondante con la rilevazione automatica
          della stanza che fa già l'AI. Una sola info: cosa fa il modo. */}
      {!allDone && mode === "empty_room" && (
        <div className="mb-5" data-testid="foto-empty-room-hint">
          <div className="bg-[#F5F4F0] border border-[#E8E5DE] rounded-md p-3 text-[12.5px] text-[#3F3D38] leading-[1.55]">
            Pronto rimuove <strong>tutti i mobili</strong> dalla foto mantenendo intatti
            pareti, pavimento, finestre e luce. Perfetto quando l'arredo del proprietario è
            datato o ingombrante e vuoi mostrare lo <strong className="text-[#7C8B6E]">spazio reale</strong>
            al compratore. Cucine fisse, sanitari e armadi a muro restano.
          </div>
        </div>
      )}

      {/* iter v30 r147 — Restyling esterno: niente style/room. Solo info su
          cosa fa la modalità. Pensato per ville/case abbandonate o datate.
          iter v32 r29 — Rimosso il toggle "casa piccola o di paese" (User:
          «chi se ne frega»). `compact` resta false di default per il backend. */}
      {!allDone && mode === "exterior_restyle" && (
        <div className="mb-5" data-testid="foto-exterior-hint">
          <div className="bg-[#F5F4F0] border border-[#E8E5DE] rounded-md p-3 text-[12.5px] text-[#3F3D38] leading-[1.55]">
            Pronto mostra la stessa casa <strong>come se fosse appena ristrutturata</strong>: intonaco
            rifatto, tetto integro, infissi nuovi, giardino curato.
            La <strong className="text-[#7C8B6E]">sagoma, i piani, le finestre e lo stile architettonico</strong> restano
            identici.
          </div>
        </div>
      )}

      {/* iter v28 r409 — User M1318: «lascia solo il migliora tutto perché
          l'altro non funziona». Tolto il segmented control: ora mostriamo
          solo una piccola riga descrittiva di cosa fa la modalità.
          iter v32 r6 — User M1671: tolto il testo sulla sfocatura quadri.
          Aggiunto disclaimer "qualche errore è possibile". */}
      {!allDone && mode === "enhance" && (
        <div className="mb-4 space-y-2" data-testid="foto-enhance-options">
          {/* iter v33 r107 · Segmented compatto Completo | Solo luce.
              Default Completo (comportamento invariato).
              iter v33 r107b · User: nascondo Solo luce dalla UI production.
              Il picker è visibile solo con REACT_APP_SHOW_SOLO_LUCE=true. */}
          {String(process.env.REACT_APP_SHOW_SOLO_LUCE || "").toLowerCase() === "true" && (
            <>
              <div
                role="tablist"
                aria-label="Modalità Migliora"
                className="inline-flex w-full sm:w-auto rounded-full bg-[#EAE7DF] p-0.5 gap-0"
                data-testid="enhance-focus-picker"
              >
                {[
                  { v: "completo",  l: "Completo",  t: "completo"  },
                  { v: "solo_luce", l: "Solo luce", t: "solo-luce" },
                ].map((opt) => {
                  const active = enhanceFocus === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setEnhanceFocus(opt.v)}
                      className={`flex-1 sm:flex-none px-4 py-1.5 rounded-full text-[12px] font-semibold transition-all ${
                        active
                          ? "bg-[#1C1C1A] text-white shadow-sm"
                          : "text-[#6B6A65] hover:text-[#1C1C1A]"
                      }`}
                      data-testid={`enhance-focus-${opt.t}`}
                    >
                      {opt.l}
                    </button>
                  );
                })}
              </div>
              <p
                className="text-[11.5px] text-[#6B6A65] leading-snug px-1"
                data-testid="enhance-focus-desc"
              >
                {enhanceFocus === "solo_luce"
                  ? "Interviene su luce, esposizione e colore."
                  : "Migliora luce, colori, prospettiva e piccoli disordini."}
              </p>
            </>
          )}
          {String(process.env.REACT_APP_SHOW_SOLO_LUCE || "").toLowerCase() !== "true" && (
            <p className="text-[11.5px] text-[#7C8B6E] leading-snug px-1" data-testid="foto-enhance-sub">
              Luce, esposizione, linee dritte, riordino. Mobili, arredo e architettura restano identici.
            </p>
          )}
          <p className="text-[10.5px] text-[#9A968B] mt-1 px-1 leading-snug inline-flex items-start gap-1.5" data-testid="foto-enhance-retry-hint">
            <ArrowsClockwise size={10} weight="fill" className="text-[#B57C5E] mt-[1px] shrink-0" />
            <span>Possiamo commettere qualche errore — se qualcosa non torna, premi <strong>&quot;Rigenera&quot;</strong>: spesso al secondo tentativo esce meglio.</span>
          </p>
        </div>
      )}

      {/* Hidden file input — multi-select */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={onPick}
        className="hidden"
        data-testid="foto-file-input"
      />

      {/* Stage 1: empty → upload prompt */}
      {!batchActive && (() => {
        // iter v28 r449 — User M1363: il testo "Carica le tue foto" era
        // identico in tutte le 5 modalità → l'utente non capiva che la sua
        // scelta su SectionOnboarding aveva avuto effetto. Ora il prompt
        // dell'uploader si adatta alla mode corrente.
        const uploaderTitle = mode === "stage" ? "Carica uno spazio vuoto o un cantiere"
          : mode === "empty_room" ? "Carica uno spazio arredato"
          : mode === "restyle" ? "Carica uno spazio arredato"
          : mode === "exterior_restyle" ? "Carica la foto della casa"
          : mode === "furnish_plan" ? "Carica una pianta catastale"
          : "Carica le tue foto";
        const uploaderHint = mode === "stage" ? `Diventerà arredata in stile ${style}`
          : mode === "empty_room" ? "Diventerà svuotata — mostri lo spazio reale"
          : mode === "restyle" ? `Diventerà rinnovata in stile ${style}`
          : mode === "exterior_restyle" ? "Diventerà 'appena ristrutturata' — stesso stile, ma curata"
          : mode === "furnish_plan" ? `Pianta 2D → arredata, vista dall'alto · stile ${style}`
          : "Luce, colori, ordine — mobili e architettura restano identici";
        return (
        <button
          ref={uploaderRef}
          onClick={openPicker}
          className="w-full aspect-[4/5] sm:aspect-[16/10] border-2 border-dashed border-[#E5E7E0] hover:border-[#7C8B6E] hover:bg-[#FFFFFF] rounded-2xl flex flex-col items-center justify-center gap-3 transition-all group"
          data-testid="foto-upload-btn"
          id="foto-uploader"
        >
          <div className="w-16 h-16 rounded-full bg-[#7C8B6E] text-[#FFFFFF] flex items-center justify-center group-hover:scale-110 transition-transform">
            <ImageIcon size={28} weight="fill" />
          </div>
          <div className="text-lg font-medium text-[#1C1C1A]">{uploaderTitle}</div>
          <div className="text-[12.5px] text-[#7C8B6E] font-semibold text-center px-6" data-testid="foto-upload-hint">
            {uploaderHint}
          </div>
          <div className="text-xs text-[#9A968B] text-center px-6">
            Scatta una foto o scegli dalla galleria. Fino a 10 foto in una volta · JPG, PNG o HEIC.
          </div>
        </button>
        );
      })()}

      {/* Stage 2-3-4: batch grid (works for 1 or many photos uniformly) */}
      {batchActive && (
        <div className="space-y-4" data-testid="foto-batch">
          {/* Single big result viewer if exactly 1 done item */}
          {singleResultItem && singleResultItem.status === "done" && (
            <ResultViewer
              item={singleResultItem}
              afterUrl={liveFileUrl(singleResultItem.result.id, "after")}
              focusLabels={(() => {
                // Build a list of human-friendly labels for the pill, based on what
                // the agent selected. Only applies to enhance mode.
                if (mode !== "enhance") return null;
                return ["Migliora tutto"];
              })()}
              modeLabel={mode === "stage" ? `Arredato · ${style}` :
                         mode === "restyle" ? `Rinnovato · ${style}` :
                         mode === "empty_room" ? "Stanza svuotata" :
                         mode === "exterior_restyle" ? "Esterno ristrutturato" :
                         mode === "furnish_plan" ? "Pianta arredata" : null}
              onRegenerate={() => {
                // iter v28 r87 + r224 — User M1144: "Riprova" NON ri-esegue
                // mai automaticamente con gli stessi parametri. Riporta
                // sempre l'utente ai controlli (Migliora / Arreda / Rinnova /
                // Pianta) per scegliere cosa fare di nuovo. Vale anche per
                // "Migliora" che prima rilanciava auto, ora apre il menu.
                setItems((prev) => prev.map((it) =>
                  it.id === singleResultItem.id
                    ? { ...it, status: "pending", result: null, error: null }
                    : it,
                ));
                // Scroll su per mostrare i controlli
                setTimeout(() => {
                  const ctrlPanel = document.querySelector('[data-testid="foto-mode-tabs"], [data-testid="foto-style-row"], [data-testid="foto-plan-hint"]');
                  if (ctrlPanel) ctrlPanel.scrollIntoView({ behavior: "smooth", block: "start" });
                  else window.scrollTo({ top: 0, behavior: "smooth" });
                }, 50);
              }}
              // iter v28 r358 — User M1267: "Non ti piace? Rigenera" =
              // ri-esegue stessa foto con stessi parametri (output diverso
              // perché il modello è stocastico).
              // iter v28 r366 — Rimosso il bottone "Rigenera in Ultra-fedele".
              onRigenera={() => regenerateOne(singleResultItem.id)}
              showUltraFedele={false}
              disabled={anyProcessing}
            />
          )}

          {/* Grid of items (always shown if >1 OR if 1 still processing/error) */}
          {!singleResultItem || singleResultItem.status !== "done" ? (
            <>
              {/* iter 275: barra "Seleziona tutto/nessuno" — visibile solo
                  quando ci sono ≥2 foto pending. Aiuta a togliere la
                  selezione in massa invece di tappare 1 per 1. */}
              {items.filter((x) => x.status === "pending").length >= 2 && (
                <div className="flex items-center justify-between mb-2 px-1">
                  <div className="text-[11px] text-[#6B6A65]">
                    {(() => {
                      const pending = items.filter((x) => x.status === "pending");
                      const selected = pending.filter((x) => !excludedIds.has(x.id)).length;
                      return `${selected} di ${pending.length} selezionate`;
                    })()}
                  </div>
                  <button
                    onClick={() => {
                      const pendingIds = items.filter((x) => x.status === "pending").map((x) => x.id);
                      const allSelected = pendingIds.every((id) => !excludedIds.has(id));
                      if (allSelected) {
                        // tutte selezionate → escludi tutte
                        setExcludedIds(new Set(pendingIds));
                      } else {
                        // non tutte → seleziona tutte (svuota esclusi)
                        setExcludedIds(new Set());
                      }
                    }}
                    className="text-[11.5px] font-semibold text-[#7C8B6E] hover:text-[#1C1C1A] underline-offset-2 hover:underline"
                    data-testid="foto-batch-select-toggle"
                  >
                    {(() => {
                      const pendingIds = items.filter((x) => x.status === "pending").map((x) => x.id);
                      const allSelected = pendingIds.every((id) => !excludedIds.has(id));
                      return allSelected ? "Deseleziona tutte" : "Seleziona tutte";
                    })()}
                  </button>
                </div>
              )}
              {/* iter 275 round 48: barra di progresso batch. Visibile
                  durante l'elaborazione (anyProcessing) e per 1.5s dopo
                  (allDone) per dare conferma. Sui batch piccoli (1-2 foto)
                  resta nascosta perché lo spinner inline basta. */}
              {items.length >= 3 && (anyProcessing || (allDone && items.some((x) => x.status === "done"))) && (() => {
                const total = items.filter((it) => !excludedIds.has(it.id)).length;
                const done = items.filter((it) => !excludedIds.has(it.id) && (it.status === "done" || it.status === "error")).length;
                const ok = items.filter((it) => !excludedIds.has(it.id) && it.status === "done").length;
                const fail = items.filter((it) => !excludedIds.has(it.id) && it.status === "error").length;
                const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                return (
                  <div
                    className="mb-3 bg-white border border-[#EAE7DF] rounded-lg px-3 py-2.5"
                    data-testid="foto-batch-progress"
                  >
                    <div className="flex items-center justify-between gap-3 mb-1.5">
                      <div className="text-[11.5px] text-[#1C1C1A]">
                        {anyProcessing ? (
                          <>Generazione in corso · <strong>{ok}/{total}</strong> {fail > 0 && <span className="text-red-600 font-semibold">· {fail} da riprovare</span>}</>
                        ) : (
                          <>Completato · <strong>{ok}/{total}</strong> {fail > 0 && <span className="text-red-600 font-semibold">· {fail} fallite</span>}</>
                        )}
                      </div>
                      <div className="text-[10.5px] tabular-nums text-[#9A968B] shrink-0">{pct}%</div>
                    </div>
                    <div className="h-1.5 rounded-full bg-[#F1F4ED] overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ease-out ${fail > 0 ? "bg-gradient-to-r from-[#7C8B6E] to-red-500" : "bg-[#7C8B6E]"}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })()}
              {/* iter 2026-02 — Toggle "Stessa stanza" per multi-foto stage.
                  Se ON, foto con lo stesso roomHint condividono groupId
                  → backend usa la prima riuscita del gruppo come reference
                  per le viste successive dello STESSO ambiente. */}
              {mode === "stage" && items.length >= 2 && !allDone && !anyProcessing && (
                <label
                  className="mb-2 flex items-center gap-2 text-[12px] text-[#1C1C1A] cursor-pointer select-none"
                  data-testid="foto-batch-group-toggle"
                >
                  <input
                    type="checkbox"
                    checked={groupSameRoom}
                    onChange={(e) => setGroupSameRoom(e.target.checked)}
                    className="w-4 h-4 accent-[#7C8B6E]"
                  />
                  <span>Foto con lo stesso ambiente sono <strong>viste della stessa stanza</strong></span>
                </label>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="foto-batch-grid">
              {items.map((it) => {
                const excluded = excludedIds.has(it.id);
                // iter 275: la tile è cliccabile per togglare la selezione,
                // SOLO se status === "pending" e ci sono almeno 2 foto. Per
                // 1 foto sola la selezione non ha senso (è sempre quella).
                const selectable = it.status === "pending" && items.length > 1;
                // iter 2026-02 — Room-hint effettivo: per-tile override →
                // global room → "" (Automatico). Mostrato nella chip.
                const effectiveHint = it.roomHint || (mode === "stage" ? room : "");
                const hintLabel = (ROOM_HINTS.find((r) => r.v === effectiveHint) || { l: "Automatico" }).l;
                return (
                <div
                  key={it.id}
                  className={`relative rounded-xl overflow-hidden bg-[#1C1C1A] aspect-square border transition-all ${
                    excluded ? "border-[#9A968B]/40" : "border-[#E5E7E0]"
                  } ${selectable ? "cursor-pointer" : ""}`}
                  onClick={selectable ? () => toggleExcluded(it.id) : undefined}
                  data-testid={`foto-batch-item-${it.status}`}
                >
                  <img
                    src={it.status === "done" && it.result ? liveFileUrl(it.result.id, "after") : it.previewUrl}
                    alt=""
                    className={`w-full h-full object-cover transition-opacity ${
                      it.status === "processing" ? "opacity-40" : excluded ? "opacity-35 grayscale" : ""
                    }`}
                  />
                  {/* iter 275: badge di selezione (visibile su pending in batch) */}
                  {selectable && (
                    <span
                      className={`absolute top-1.5 left-1.5 w-5 h-5 aspect-square shrink-0 rounded-full border-2 inline-flex items-center justify-center transition-all ${
                        excluded
                          ? "bg-white/80 border-white/80"
                          : "bg-[#7C8B6E] border-white shadow-sm"
                      }`}
                      style={{ minWidth: "20px", minHeight: "20px", width: "20px", height: "20px" }}
                      data-testid={`foto-select-${it.id}`}
                      aria-label={excluded ? "Includi nel batch" : "Selezionata"}
                    >
                      {!excluded && <Check size={11} weight="bold" className="text-white" />}
                    </span>
                  )}
                  {/* Status overlay */}
                  {it.status === "processing" && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white gap-1.5">
                      <Spinner size={22} className="animate-spin" />
                      <span className="text-[10px] uppercase tracking-[0.16em] font-semibold">In corso</span>
                    </div>
                  )}
                  {it.status === "done" && (
                    <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] text-white bg-[#7C8B6E]/85 backdrop-blur-sm px-1.5 py-0.5 rounded">
                      <Sparkle size={9} weight="fill" /> Pronta
                    </span>
                  )}
                  {it.status === "error" && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white gap-1 bg-red-900/70 px-2 text-center">
                      <X size={20} weight="bold" />
                      <span className="text-[9.5px] leading-tight">{it.error?.slice(0, 60)}</span>
                      {/* iter 275 round 47: bottone Riprova inline così
                          l'utente rilancia solo la foto fallita senza dover
                          rifare tutto il batch. */}
                      <button
                        onClick={(e) => { e.stopPropagation(); regenerateOne(it.id); }}
                        className="mt-1 text-[10px] font-semibold uppercase tracking-[0.1em] bg-white text-[#1C1C1A] hover:bg-[#F1F4ED] px-2.5 py-1 rounded-full transition-colors"
                        data-testid={`foto-batch-retry-${it.id}`}
                      >
                        Riprova
                      </button>
                    </div>
                  )}
                  {/* iter v28 r88 — Rinominato aria-label "Rimuovi" → "Rimuovi dalla coda"
                      per coerenza col tab Foto annuncio dove abbiamo distinto
                      "X chiude editor" da "Elimina dall'annuncio". Qui rimuove
                      solo dalla coda batch corrente: la foto originale resta
                      nel device dell'utente, può ricaricarla. */}
                  {it.status !== "processing" && (
                    <button
                      onClick={(e) => { e.stopPropagation(); removeItem(it.id); }}
                      className="absolute top-1.5 right-1.5 w-6 h-6 aspect-square rounded-full bg-[#1C1C1A]/70 backdrop-blur-sm text-white flex items-center justify-center hover:bg-red-600/80 transition-colors appearance-none flex-shrink-0 p-0"
                      style={{ width: 24, height: 24, minWidth: 24, minHeight: 24 }}
                      aria-label="Rimuovi dalla coda"
                      title="Rimuovi dalla coda · la foto sul tuo device resta"
                      data-testid={`foto-batch-remove-${it.id}`}
                    >
                      <X size={11} weight="bold" />
                    </button>
                  )}
                  {/* Quick actions for done items: download + share */}
                  {it.status === "done" && it.result && (
                    <div className="absolute bottom-1.5 right-1.5 flex items-center gap-1.5">
                      <button
                        onClick={() => downloadOne(it.result.id)}
                        className="w-7 h-7 rounded-full bg-white text-[#1C1C1A] flex items-center justify-center hover:bg-[#F1F4ED] transition-colors shadow-sm"
                        aria-label="Scarica"
                        data-testid={`foto-batch-dl-${it.id}`}
                      >
                        <DownloadSimple size={13} weight="bold" />
                      </button>
                      <button
                        onClick={() => shareImage({
                          url: liveFileUrl(it.result.id, "after"),
                          filename: `pronto-foto-${it.result.id.slice(0, 8)}.jpg`,
                        })}
                        className="w-7 h-7 rounded-full bg-[#7C8B6E] text-white flex items-center justify-center hover:bg-[#5C6E50] transition-colors shadow-sm"
                        aria-label="Condividi"
                        data-testid={`foto-batch-share-${it.id}`}
                      >
                        <Share size={12} weight="bold" />
                      </button>
                    </div>
                  )}
                  {/* iter 2026-02 — Chip categoria per-foto (SOLO mode=stage,
                      SOLO su pending, SOLO se ≥2 foto). Tap → apre picker. */}
                  {mode === "stage" && items.length >= 2 && it.status === "pending" && !excluded && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setRoomPickerFor(it.id); }}
                      className="absolute bottom-1.5 left-1.5 text-[10px] font-semibold bg-white/95 backdrop-blur-sm text-[#1C1C1A] px-2 py-1 rounded-full inline-flex items-center gap-1 hover:bg-white shadow-sm transition-all"
                      data-testid={`foto-item-room-${it.id}`}
                      title="Che ambiente vuoi creare da questa foto?"
                    >
                      <House size={10} weight="bold" className="text-[#7C8B6E]" /> {hintLabel}
                    </button>
                  )}
                </div>
                );
              })}
              {/* Add-more tile */}
              {items.length < 10 && !anyProcessing && (
                <button
                  onClick={openPicker}
                  className="aspect-square rounded-xl border-2 border-dashed border-[#E5E7E0] hover:border-[#7C8B6E] hover:bg-white text-[#6B6A65] hover:text-[#1C1C1A] flex flex-col items-center justify-center gap-1 transition-all"
                  data-testid="foto-batch-add"
                >
                  <ImageIcon size={20} weight="duotone" />
                  <span className="text-[10px] uppercase tracking-[0.16em] font-semibold">Aggiungi</span>
                </button>
              )}
              </div>
            </>
          ) : null}

          {/* iter v32 r37e — Banner one-time iOS: avvisa che l'app va in pausa
              in background durante la generazione. Visibile solo prima volta. */}
          {iosBgBannerVisible && !allDone && (
            <div
              className="rounded-xl border border-[#E8C77F]/60 bg-[#FDF6E3] px-3.5 py-2.5 flex items-start gap-2.5"
              data-testid="foto-ios-bg-banner"
            >
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[#E8C77F] text-[#5C4314] flex items-center justify-center text-[12px] font-bold mt-0.5">!</div>
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] font-semibold text-[#5C4314] leading-snug">
                  Resta su questa pagina.
                </div>
                <div className="text-[11.5px] text-[#7A5E2E] leading-snug mt-0.5">
                  iOS mette in pausa l&apos;app quando passi ad altre. La generazione si interrompe.
                </div>
              </div>
            </div>
          )}

          {/* Action bar */}
          <div className="grid grid-cols-2 gap-2.5">
            {/* Generate / Re-run on errors */}
            {!allDone && (() => {
              // iter 275: conta solo le pending NON escluse per il counter CTA
              const toRun = items.filter((x) => x.status === "pending" && !excludedIds.has(x.id));
              const hasSelection = items.length > 1 && excludedIds.size > 0;
              // iter 2026-02 — Cost preview per multi-foto stage: N × 5 crediti
              const stageBatchCost = mode === "stage" && toRun.length > 1
                ? toRun.length * 5
                : null;
              const cta = items.length === 1
                ? (mode === "enhance" ? "Genera · migliora"
                  : mode === "restyle" ? `Genera · rinnova in ${style}`
                  : mode === "empty_room" ? "Genera · svuota spazio"
                  : mode === "exterior_restyle" ? "Genera · restyling esterno"
                  : mode === "furnish_plan" ? `Genera · arreda pianta in ${style}`
                  : `Genera · arreda in ${style}`)
                : stageBatchCost !== null
                  ? `Arreda tutte (${toRun.length} × 5 = ${stageBatchCost} crediti)`
                  : hasSelection
                    ? `Genera selezionate (${toRun.length})`
                    : `Genera tutte (${items.length})`;
              return (
              <button
                onClick={runAi}
                disabled={submitting || anyProcessing || toRun.length === 0}
                className="col-span-2 bg-[#1C1C1A] text-[#FFFFFF] py-3.5 px-4 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#2A2A28] active:scale-[0.99] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="foto-generate-btn"
              >
                {(submitting || anyProcessing) ? (
                  <><Spinner size={18} className="animate-spin" /> Sto generando ({items.filter((x) => x.status === "done").length}/{items.length})...</>
                ) : (
                  <><Sparkle size={18} weight="fill" />{cta}</>
                )}
              </button>
              );
            })()}

            {/* All done — show download all + share (single only) + new */}
            {allDone && (
              <>
                {items.filter((x) => x.status === "done").length > 1 && (
                  <button
                    onClick={downloadAll}
                    className="col-span-2 bg-[#1C1C1A] text-[#FFFFFF] py-3 px-4 rounded-full text-sm font-medium inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                    data-testid="foto-download-all"
                  >
                    <DownloadSimple size={18} weight="bold" /> Scarica tutte ({items.filter((x) => x.status === "done").length})
                  </button>
                )}
                {singleResultItem && singleResultItem.status === "done" && (
                  <>
                    {/* iter 255+258: deep-link return — torna alla pagina
                        chiamante (galleria-picker O property-detail). Label
                        adattata in base al return URL. */}
                    {returnUrl && (
                      <button
                        onClick={() => {
                          toast.success("Foto rilavorata · ora torna all'annuncio", { duration: 2500 });
                          navigate(returnUrl);
                        }}
                        className="col-span-2 bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white py-3 px-4 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors"
                        data-testid="foto-return-to-picker"
                      >
                        <ArrowLeft size={16} weight="bold" /> {returnUrl.includes("/property/") ? "Torna all'annuncio" : "Torna al picker · aggiungila all'annuncio"}
                      </button>
                    )}
                    {/* iter v31 r10 — Restored 3-dots menu. L'action bar
                        ora ha 4 pillole essenziali (Salva · Condividi ·
                        Originale · ⋯). Il "⋯" apre un bottom sheet con
                        le azioni secondarie: Card 1080×1350, Video 6s,
                        Storia social, ELIMINA FOTO. Prima Card/Video erano
                        inline ma occupavano spazio e non c'era posto per
                        "Elimina" (lamentela utente). */}
                    <div className="col-span-2 bg-[#1C1C1A] rounded-2xl shadow-lg p-1.5 max-w-2xl mx-auto w-full">
                      <div className="grid grid-cols-4 gap-1">
                        <button
                          onClick={() => downloadOne(singleResultItem.result.id)}
                          className="bg-white text-[#1C1C1A] py-2.5 px-1.5 rounded-full text-[10.5px] font-semibold inline-flex items-center justify-center gap-1 hover:bg-[#F1F4ED] transition-colors leading-tight"
                          data-testid="foto-download"
                          title="Scarica la foto modificata"
                        >
                          <DownloadSimple size={13} weight="bold" className="shrink-0" />
                          <span>Salva</span>
                        </button>
                        <button
                          onClick={shareWhatsapp}
                          className="bg-white text-[#1C1C1A] py-2.5 px-1.5 rounded-full text-[10.5px] font-semibold inline-flex items-center justify-center gap-1 hover:bg-[#F1F4ED] transition-colors leading-tight"
                          data-testid="foto-share-after"
                          title="Condividi via WhatsApp, Email, AirDrop…"
                        >
                          <Share size={13} weight="bold" className="shrink-0" />
                          <span>Condividi</span>
                        </button>
                        <button
                          onClick={() => downloadOne(singleResultItem.result.id, "before")}
                          className="bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white py-2.5 px-1.5 rounded-full text-[10.5px] font-semibold inline-flex items-center justify-center gap-1 transition-colors leading-tight"
                          data-testid="foto-download-before"
                          title="Salva l'originale (prima)"
                        >
                          <ImageIcon size={13} weight="bold" className="shrink-0" />
                          <span>Originale</span>
                        </button>
                        <button
                          onClick={() => setMoreOpen(true)}
                          className="bg-[#C8A66B] hover:bg-[#B89656] text-white py-2.5 px-1.5 rounded-full text-[10.5px] font-semibold inline-flex items-center justify-center gap-1 transition-colors leading-tight"
                          data-testid="foto-more-btn"
                          title="Altro · card, video, elimina"
                          aria-label="Altre azioni"
                        >
                          <DotsThreeVertical size={16} weight="bold" className="shrink-0" />
                          <span>Altro</span>
                        </button>
                      </div>
                    </div>
                    {/* iter v31 r10 — Vecchio details "Altre azioni · storia
                        social" rimosso: ora tutto è nel bottom sheet che apre
                        il bottone "⋯". */}
                  </>
                )}
                <button
                  onClick={() => { reset(); openPicker(); }}
                  className="col-span-2 bg-[#F1F4ED] text-[#1C1C1A] py-3 px-4 rounded-full text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-[#E5E7E0] transition-all"
                  data-testid="foto-new"
                >
                  <ImageIcon size={18} weight="bold" /> Nuove foto
                </button>
              </>
            )}

            {/* Cancel — only when nothing processed yet */}
            {!allDone && !anyProcessing && (
              <button
                onClick={reset}
                className="col-span-2 text-xs text-[#6B6A65] hover:text-[#1C1C1A] inline-flex items-center justify-center gap-1.5 py-2"
                data-testid="foto-cancel"
              >
                <X size={12} /> Annulla tutto
              </button>
            )}
          </div>

          {!allDone && !anyProcessing && (
            <p className="text-[11px] text-[#6B6A65] text-center">
              Cambia modalità o stile qui sopra prima di generare.
            </p>
          )}
        </div>
      )}

      {/* Free-tier upgrade nudge — iter v26 r13:
          • spostato in FONDO (era prima della galleria, ora dopo)
          • copy aggiornato: ~400 crediti/mese su Pro, non "illimitate" */}
      {history.length > 0 && (
        <section className="mt-10 pt-8 border-t border-[#EAE7DF]" data-testid="foto-history">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <div>
              <h2 className="text-[15px] font-semibold text-[#1C1C1A]">La tua galleria</h2>
              <p className="text-[11.5px] text-[#9A968B] mt-0.5">Riscarica, ricondividi o usa una foto in un annuncio</p>
            </div>
            <div className="flex items-center gap-2">
              {/* iter v26 r10 — Bottone Seleziona per multi-selezione history */}
              <button
                onClick={() => {
                  if (historySelectMode) {
                    setHistorySelectMode(false);
                    setHistorySelected({});
                  } else {
                    setHistorySelectMode(true);
                  }
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-semibold transition-colors ${
                  historySelectMode
                    ? "bg-[#1C1C1A] text-white hover:bg-black"
                    : "bg-white border border-[#EAE7DF] text-[#1C1C1A] hover:border-[#7C8B6E] hover:bg-[#F1F4ED]"
                }`}
                data-testid="foto-history-toggle-select"
              >
                <Check size={12} weight="bold" /> {historySelectMode ? "Annulla" : "Seleziona"}
              </button>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#9A968B] font-semibold">{history.length}</span>
            </div>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2">
            {history.slice(0, 5).map((h) => {
              // Shape from /api/gallery: { id: "live:xxx" | "prop:xxx", source, kind, url, property_id, property_title, ... }
              const isLive = h.source === "live";
              const realId = (h.id || "").replace(/^(live|prop):/, "");
              const imgSrc = galleryItemUrl(h);
              const downloadFn = isLive
                ? () => downloadOne(realId)
                : async () => {
                    // iter v28 r472 — device-aware: mobile → share sheet
                    // (unico modo per finire in Galleria Foto su iOS),
                    // desktop → download blob diretto.
                    const filename = `pronto-foto-${realId.slice(0, 8)}.jpg`;
                    const isMobile = typeof navigator !== "undefined" && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
                    if (isMobile) {
                      await shareImage({ url: imgSrc, filename, title: "", text: "" });
                      return;
                    }
                    try {
                      const res = await fetch(imgSrc);
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = filename;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(url);
                    } catch {
                      toast.error("Download non riuscito");
                    }
                  };
              const kindLabel = h.kind === "stage"
                ? "Arredata"
                : h.kind === "restyle"
                ? "Rinnovata"
                : h.kind === "furnish_plan"
                ? "Pianta arredata"
                : "Migliorata";
              return (
              <div
                key={h.id}
                className={`group relative aspect-square rounded-lg overflow-hidden bg-[#F1F4ED] border-2 transition-all cursor-pointer ${
                  historySelected[h.id]
                    ? "border-[#7C8B6E] ring-2 ring-[#7C8B6E]/30"
                    : "border-[#EAE7DF] hover:border-[#7C8B6E]"
                }`}
                onClick={() => {
                  // iter v26 r10 — in selectMode tap = toggle, altrimenti lightbox
                  if (historySelectMode) toggleHistorySelected(h.id);
                  else setLightboxItem(h);
                }}
                data-testid={`foto-history-${h.id}`}
                data-selected={historySelected[h.id] ? "true" : "false"}
              >
                <img
                  src={imgSrc}
                  alt={h.kind}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  onError={(e) => {
                    // Storia: file rimosso dall'object store. Nascondi l'img,
                    // resta il bg sage placeholder + la label kind in basso.
                    e.currentTarget.style.display = "none";
                    const ph = e.currentTarget.nextElementSibling;
                    if (ph && ph.dataset && ph.dataset.placeholder === "1") ph.style.display = "flex";
                  }}
                />
                <div
                  data-placeholder="1"
                  style={{ display: "none" }}
                  className="absolute inset-0 items-center justify-center text-[#7C8B6E]/40"
                >
                  <ImageIcon size={28} weight="light" />
                </div>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-1.5">
                  <div className="text-[9px] uppercase tracking-[0.14em] text-white/95 font-semibold truncate">
                    {kindLabel}{h.property_title ? ` · ${h.property_title}` : ""}
                  </div>
                </div>
                {/* iter 275 round 34: squircle iOS extracted in
                    FotoLightboxParts. Singola icona elegante grigio scuro.
                    Nascosta in selectMode (la check circle prende il posto). */}
                {!historySelectMode && <PhotoTileIcon Icon={ArrowsOutSimple} />}

                {/* iter v26 r10 — check circle visibile solo in selectMode */}
                {historySelectMode && (
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleHistorySelected(h.id); }}
                    className={`absolute top-1.5 left-1.5 w-7 h-7 rounded-full inline-flex items-center justify-center transition-all shadow-md ${
                      historySelected[h.id]
                        ? "bg-[#7C8B6E] border-2 border-white text-white scale-110"
                        : "bg-white/95 border-2 border-white text-transparent hover:text-[#7C8B6E]/40"
                    }`}
                    data-testid={`foto-history-select-${h.id}`}
                    aria-label={historySelected[h.id] ? "Deseleziona" : "Seleziona"}
                  >
                    <Check size={13} weight="bold" />
                  </button>
                )}
                {/* iter 275: rimossi i 3 pulsantini hover-only (scarica/condividi/usa)
                    su mobile non si vedevano e su desktop erano confusi. Ora tap
                    sulla tile apre il lightbox con i 3 bottoni grandi e chiari. */}
                {/* iter 275: Import picker · ora è un modale FIXED full-screen
                    e non più `absolute inset-0` dentro la tile (era 150×150 px
                    su mobile, illeggibile e si "impallava"). */}
                {importingId === h.id && isLive && (
                  <div
                    className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center sm:p-4 z-50"
                    onClick={(e) => { e.stopPropagation(); setImportingId(null); }}
                    data-testid={`foto-history-import-picker-${h.id}`}
                  >
                    <div
                      className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[80dvh]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="sticky top-0 z-10 px-5 py-4 border-b border-[#E5E7E0] bg-white flex items-start justify-between gap-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-semibold">Usa in un annuncio</div>
                          <div className="text-[15px] font-semibold text-[#1C1C1A] mt-0.5">Scegli l'annuncio</div>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); setImportingId(null); }}
                          className="w-9 h-9 rounded-full bg-white border border-[#E5E7E0] hover:border-[#1C1C1A] hover:bg-[#F1F4ED] flex items-center justify-center text-[#1C1C1A] flex-shrink-0 shadow-sm"
                          data-testid={`foto-history-import-close-${h.id}`}
                          aria-label="Chiudi"
                        >
                          <X size={18} weight="bold" />
                        </button>
                      </div>
                      <div className="p-4 overflow-y-auto space-y-1.5">
                        {properties.filter((p) => !p.is_demo).map((p) => (
                          <button
                            key={p.id}
                            onClick={(e) => { e.stopPropagation(); importToProperty(realId, p.id); }}
                            disabled={importBusyPid === p.id}
                            className="w-full text-left px-3 py-3 text-[13px] text-[#1C1C1A] bg-[#FAF9F5] hover:bg-[#F1F4ED] border border-[#E8E5DE] hover:border-[#7C8B6E] rounded-lg transition-colors disabled:opacity-50 inline-flex items-center gap-2.5"
                            data-testid={`foto-history-import-pick-${p.id}`}
                          >
                            {importBusyPid === p.id ? (
                              <Spinner size={14} className="animate-spin shrink-0 text-[#7C8B6E]" />
                            ) : (
                              <House size={14} weight="fill" className="text-[#7C8B6E] shrink-0" />
                            )}
                            <span className="truncate font-medium">{p.title || "Senza titolo"}</span>
                          </button>
                        ))}
                        {properties.filter((p) => !p.is_demo).length === 0 && (
                          <div className="px-2 py-6 text-[12px] text-[#9A968B] italic text-center">
                            Nessun annuncio ancora.<br/>
                            <a href="/dashboard/property/new" className="text-[#7C8B6E] hover:underline">Crea il primo →</a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>

          {/* iter v28 r459 — User M1376: «galleria nell'editor max 5 immagini,
              se no lo mandi a vedere la sezione galleria». Mostra link
              "Vedi tutta la galleria →" se ci sono più di 5 elementi. */}
          {history.length > 5 && (
            <div className="mt-4 text-center">
              <button
                onClick={() => navigate("/dashboard/galleria")}
                className="inline-flex items-center gap-1.5 text-[12.5px] text-[#7C8B6E] hover:text-[#5C6E50] font-semibold underline underline-offset-2"
                data-testid="foto-history-see-all"
              >
                Vedi tutta la galleria ({history.length} foto)
                <ArrowRight size={12} weight="bold" />
              </button>
              <div className="mt-1.5 text-[10.5px] text-[#9A968B]">
                Le foto sono conservate per 30 giorni dalla creazione.
              </div>
            </div>
          )}
        </section>
      )}

      {/* iter v26 r13 — Free-tier upgrade nudge spostato IN FONDO (era prima
          della galleria, ora dopo). Copy aggiornato: il piano Top non è
          "illimitato" ma ~400 crediti/mese.
          iter 2026-06-12 Apple 3.1.1: nascosto su iOS native. */}
      {!isPro && !isReaderMode && quota && quota.remaining <= 1 && (
        <div className="mt-8 p-5 bg-gradient-to-br from-[#F1F4ED] to-white border border-[#E5E7E0] rounded-2xl text-center" data-testid="foto-upgrade-nudge">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E] mb-1.5">
            Hai finito il budget di oggi
          </div>
          <div className="text-base font-serif text-[#1C1C1A] leading-snug">
            {quota.remaining === 0 ? "Le foto di oggi sono esaurite" : "Ti resta 1 foto oggi"}
          </div>
          <div className="text-[12.5px] text-[#6B6A65] mt-1.5 mb-4 max-w-md mx-auto leading-relaxed">
            Con il piano Pro ottieni 320 crediti al mese, brochure vector, social pack e zero filigrana Pronto sui contenuti.
          </div>
          <button
            onClick={() => navigate("/pricing")}
            className="text-[13px] bg-[#7C8B6E] hover:bg-[#5C6E50] text-white font-semibold px-5 py-2.5 rounded-full transition-colors"
            data-testid="foto-upgrade-btn"
          >
            Passa a Pro · 49€/mese
          </button>
        </div>
      )}

      {/* iter v28 r449 — Marchetta GFB Visualization in fondo all'editor:
          quando serve rendering 3D fotorealistico (nuove costruzioni,
          ristrutturazioni profonde) Pronto non è la risposta giusta. Linkiamo
          un partner specializzato invece di lasciare l'agente senza opzioni.
          iter v30 r099 — User: «metti leggermente più visibile la marchetta
          rendering fotorealistici». Riquadro caldo (cream + sage border)
          invece del testo grigio piccolo. */}
      <a
        href="https://www.gfbvisualization.com"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-10 block bg-[#F5F0E6] border border-[#D9CFB8] rounded-xl p-5 sm:p-6 hover:shadow-md hover:border-[#7C8B6E] transition-all group"
        data-testid="foto-gfb-marchetta"
      >
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-lg bg-[#1C1C1A] text-white flex items-center justify-center shrink-0">
            <Sparkle size={22} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#9A8650] font-semibold">
              Rendering 3D fotorealistici
            </p>
            <h3 className="font-editorial text-[20px] sm:text-[22px] text-[#1C1C1A] mt-1 leading-tight">
              Nuove costruzioni o ristrutturazioni profonde?
            </h3>
            <p className="text-[13px] text-[#5E4F2F] mt-1.5 leading-relaxed">
              Per render fotorealistici realizzati su misura a partire da planimetria, Pronto rimanda al partner di riferimento:{" "}
              <span className="text-[#1C1C1A] font-semibold underline underline-offset-2 group-hover:text-[#7C8B6E] transition-colors" data-testid="foto-gfb-link">
                GFB Visualization →
              </span>
            </p>
          </div>
        </div>
      </a>

      {/* iter v26 r10 — Sticky bar multi-selezione history (foto già lavorate).
          Stesso pattern di GalleriaFoto/FotoTab per coerenza:
          - "Tutte (X)" per seleziona tutto / "Deseleziona" per pulire
          - Riprova: VALIDATION client-side, rifiuta mix foto+pianta
          - Scarica ZIP / Cancella */}
      {historySelectMode && historySelectedCount > 0 && (
        <>
          <div aria-hidden="true" className="h-24" />
          <div
            className="fixed left-2 right-2 z-50 px-3 py-2.5 bg-[#1C1C1A] text-white rounded-2xl shadow-2xl flex items-center gap-2 flex-wrap animate-in slide-in-from-bottom-4 duration-200"
            style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
            data-testid="foto-history-bulk-bar"
          >
            <span className="text-[12.5px] font-semibold flex-shrink-0 inline-flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-[#7C8B6E] inline-flex items-center justify-center text-[10px] font-bold">
                {historySelectedCount}
              </span>
              <span className="hidden sm:inline">{historySelectedCount === 1 ? "foto selezionata" : "foto selezionate"}</span>
              <span className="sm:hidden">{historySelectedCount === 1 ? "selezionata" : "selezionate"}</span>
            </span>
            <button
              onClick={() => {
                const allIds = history.slice(0, 20).map((h) => h.id);
                const allOn = allIds.every((id) => historySelected[id]);
                if (allOn) setHistorySelected({});
                else {
                  const sel = {};
                  allIds.forEach((id) => { sel[id] = true; });
                  setHistorySelected(sel);
                }
              }}
              className="text-[11.5px] text-white/75 hover:text-white underline underline-offset-2"
              data-testid="foto-history-select-all"
            >
              {history.slice(0, 20).every((h) => historySelected[h.id]) ? "Deseleziona" : `Tutte (${Math.min(history.length, 20)})`}
            </button>
            <div className="flex-1 hidden sm:block" />
            <div className="flex items-center gap-1.5 ml-auto flex-wrap justify-end">
              <button
                onClick={() => {
                  // iter v26 r10 — VALIDATION: Riprova non può mischiare
                  // piante (furnish_plan) con foto normali. L'AI lavora i 2
                  // input in modo radicalmente diverso, quindi avvisiamo e
                  // chiediamo di scegliere uno solo dei due tipi.
                  const selected = history.filter((h) => historySelected[h.id]);
                  const piante = selected.filter((h) => h.kind === "furnish_plan");
                  const foto = selected.filter((h) => h.kind !== "furnish_plan");
                  if (piante.length > 0 && foto.length > 0) {
                    toast.error(
                      `Hai selezionato ${foto.length} ${foto.length === 1 ? "foto" : "foto"} e ${piante.length} ${piante.length === 1 ? "pianta" : "piante"}. ` +
                      `Per riprovare insieme devono essere tutte dello stesso tipo: togli ${piante.length <= foto.length ? "le piante" : "le foto"} dalla selezione.`,
                      { duration: 6000 },
                    );
                    return;
                  }
                  const ids = selected.map((h) => h.id);
                  if (ids.length === 0) return;
                  // Mode dedotto automaticamente in FotoEditor reeditMany useEffect
                  const qs = new URLSearchParams({ reeditMany: ids.join(",") });
                  navigate(`/dashboard/foto?${qs.toString()}`);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white text-[12px] font-semibold transition-colors"
                data-testid="foto-history-bulk-riprova"
                title="Apri le foto selezionate nell'Editor per riprovarle"
              >
                <ArrowsClockwise size={12} weight="bold" /> Riprova
              </button>
              <button
                onClick={async () => {
                  const selected = history.filter((h) => historySelected[h.id]);
                  if (selected.length === 0) return;
                  toast.loading(`Scarico ${selected.length} foto…`, { id: "history-zip" });
                  try {
                    for (const h of selected) {
                      const isLive = h.source === "live";
                      const realId = (h.id || "").replace(/^(live|prop):/, "");
                      const imgSrc = galleryItemUrl(h);
                      try {
                        const res = await fetch(imgSrc);
                        const blob = await res.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `pronto-foto-${realId.slice(0, 8)}.jpg`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        URL.revokeObjectURL(url);
                      } catch { /* skip singola */ }
                    }
                    toast.success(`${selected.length} foto scaricate`, { id: "history-zip" });
                    setHistorySelected({});
                    setHistorySelectMode(false);
                  } catch {
                    toast.error("Download non riuscito", { id: "history-zip" });
                  }
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-[12px] font-semibold transition-colors"
                data-testid="foto-history-bulk-download"
              >
                <DownloadSimple size={12} weight="bold" /> {historySelectedCount === 1 ? "Scarica" : "ZIP"}
              </button>
              <button
                onClick={async () => {
                  const selected = history.filter((h) => historySelected[h.id]);
                  const n = selected.length;
                  if (n === 0) return;
                  const ok = await new Promise((resolve) => {
                    let decided = false;
                    const decide = (v) => { if (!decided) { decided = true; resolve(v); } };
                    toast.warning(
                      `Cancellare ${n} ${n === 1 ? "foto" : "foto"}? Non è reversibile.`,
                      {
                        duration: 10000,
                        action: { label: "Cancella", onClick: () => decide(true) },
                        cancel: { label: "Annulla", onClick: () => decide(false) },
                        onDismiss: () => decide(false),
                        onAutoClose: () => decide(false),
                      },
                    );
                  });
                  if (!ok) return;
                  // Delete via handler condiviso `_doDeleteHistory`
                  for (const h of selected) {
                    try {
                      await _doDeleteHistory(h.id);
                    } catch { /* skip */ }
                  }
                  toast.success(`${n} ${n === 1 ? "foto cancellata" : "foto cancellate"}`);
                  setHistorySelected({});
                  setHistorySelectMode(false);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-600/80 hover:bg-red-600 text-white text-[12px] font-semibold transition-colors"
                data-testid="foto-history-bulk-delete"
              >
                <Trash size={12} weight="bold" /> Cancella
              </button>
            </div>
          </div>
        </>
      )}

      {/* Annuncio Lampo modal — minima per creare draft + andare al Social Studio */}
      {lampoOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3 sm:p-6"
          onClick={() => !lampoBusy && setLampoOpen(false)}
          data-testid="foto-lampo-modal"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-[420px] p-5 sm:p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E] mb-1">
                  Annuncio lampo
                </div>
                <h2 className="text-lg font-semibold text-[#1C1C1A] leading-tight">
                  Storia social al volo
                </h2>
                <p className="text-[12px] text-[#6B6A65] mt-1.5 leading-snug">
                  Tre campi e Pronto ti porta nell&apos;Editor social con la foto già pronta. Senza creare un annuncio completo.
                </p>
              </div>
              <button
                onClick={() => !lampoBusy && setLampoOpen(false)}
                className="text-[#9A968B] hover:text-[#1C1C1A] p-1"
                aria-label="Chiudi"
                data-testid="foto-lampo-close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#6B6A65] mb-1 block">
                  Titolo <span className="text-[#C24E4E]">*</span>
                </label>
                <input
                  type="text"
                  value={lampoTitle}
                  onChange={(e) => setLampoTitle(e.target.value)}
                  placeholder="es. Trilocale ristrutturato"
                  className="w-full px-3 py-2.5 border border-[#E5E7E0] rounded-lg text-[14px] focus:outline-none focus:border-[#7C8B6E]"
                  data-testid="foto-lampo-title"
                  autoFocus
                  maxLength={80}
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#6B6A65] mb-1 block">
                  Città
                </label>
                <input
                  type="text"
                  value={lampoCity}
                  onChange={(e) => setLampoCity(e.target.value)}
                  placeholder="es. Milano · Brera"
                  className="w-full px-3 py-2.5 border border-[#E5E7E0] rounded-lg text-[14px] focus:outline-none focus:border-[#7C8B6E]"
                  data-testid="foto-lampo-city"
                  maxLength={60}
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#6B6A65] mb-1 block">
                  Prezzo (€)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={lampoPrice}
                  onChange={(e) => setLampoPrice(e.target.value)}
                  placeholder="es. 890000"
                  className="w-full px-3 py-2.5 border border-[#E5E7E0] rounded-lg text-[14px] focus:outline-none focus:border-[#7C8B6E]"
                  data-testid="foto-lampo-price"
                />
              </div>
            </div>

            <div className="mt-5 flex items-center gap-2">
              <button
                onClick={() => !lampoBusy && setLampoOpen(false)}
                disabled={lampoBusy}
                className="flex-1 py-2.5 rounded-full text-[13px] font-medium text-[#6B6A65] hover:bg-[#F1F4ED] disabled:opacity-50"
                data-testid="foto-lampo-cancel"
              >
                Annulla
              </button>
              <button
                onClick={createLampo}
                disabled={lampoBusy || !lampoTitle.trim()}
                className="flex-[2] py-2.5 rounded-full bg-[#7C8B6E] hover:bg-[#90A082] text-white text-[13px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
                data-testid="foto-lampo-confirm"
              >
                {lampoBusy ? (
                  <><Spinner size={14} className="animate-spin" /> Creo…</>
                ) : (
                  <><Sparkle size={14} weight="fill" /> Apri Editor social</>
                )}
              </button>
            </div>

            <p className="text-[10.5px] text-[#9A968B] mt-3 italic leading-snug text-center">
              Crea un annuncio in bozza con i campi minimi. Lo puoi completare dopo, oppure usarlo solo per i social.
            </p>
          </div>
        </div>
      )}

      {/* iter 275 round 35: lightbox foto estratto in
          /components/photo/FotoEditorLightbox.jsx. Riceve handler via props,
          stato resta nel parent → zero risk regressioni. */}
      <FotoEditorLightbox
        item={lightboxItem}
        view={lightboxView}
        setView={setLightboxView}
        onClose={() => setLightboxItem(null)}
        properties={properties}
        downloadOne={downloadOne}
        shareImage={shareImage}
        setImportingId={setImportingId}
        reopenInEditor={reopenInEditor}
        onDeleteRequest={setHistoryDeleteConfirm}
      />

      {/* iter 275 round 19: modale conferma cancellazione foto history */}
      {historyDeleteConfirm && (
        <div
          className="fixed inset-0 z-[70] bg-black/60 flex items-end sm:items-center justify-center p-3"
          onClick={() => setHistoryDeleteConfirm(null)}
          data-testid="foto-history-delete-backdrop"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-sm rounded-t-2xl sm:rounded-xl shadow-2xl"
            data-testid="foto-history-delete-modal"
          >
            <div className="p-5">
              <div className="w-11 h-11 rounded-full bg-red-50 flex items-center justify-center mb-3">
                <Trash size={20} weight="bold" className="text-red-600" />
              </div>
              <h3 className="font-editorial text-xl text-[#1C1C1A] leading-tight">
                Cancellare questa foto?
              </h3>
              <p className="text-[13px] text-[#6B6A65] mt-2 leading-relaxed">
                La foto {historyDeleteConfirm.label} verrà rimossa dalla tua galleria.
                L'azione non è reversibile.
              </p>
            </div>
            <div className="flex gap-2 p-3 border-t border-[#F0EFE9]">
              <button
                onClick={() => setHistoryDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 rounded-md text-[13px] font-semibold text-[#1C1C1A] bg-[#F1F4ED] hover:bg-[#E5E7E0] transition-colors"
                data-testid="foto-history-delete-cancel"
              >
                Annulla
              </button>
              <button
                onClick={async () => {
                  const id = historyDeleteConfirm.id;
                  setHistoryDeleteConfirm(null);
                  await _doDeleteHistory(id);
                }}
                className="flex-1 px-4 py-2.5 rounded-md text-[13px] font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors inline-flex items-center justify-center gap-1.5"
                data-testid="foto-history-delete-confirm"
              >
                <Trash size={13} weight="bold" /> Cancella
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iter v31 r10 — Sheet "Altre azioni" del risultato singolo.
          Contiene: Card 1080×1350, Video 6s, Storia social, ELIMINA foto.
          Sostituisce il vecchio details inline "Altre azioni · storia social"
          + i bottoni Card / Video 6s che occupavano l'action bar principale. */}
      {moreOpen && singleResultItem?.result && (
        <div
          className="fixed inset-0 z-[70] bg-black/55 flex items-end sm:items-center justify-center"
          onClick={() => setMoreOpen(false)}
          data-testid="foto-more-sheet-backdrop"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden"
            data-testid="foto-more-sheet"
          >
            <div className="px-5 py-4 border-b border-[#EAE7DF] flex items-center justify-between">
              <div className="font-editorial text-lg text-[#1C1C1A]">Altro</div>
              <button
                onClick={() => setMoreOpen(false)}
                className="w-8 h-8 rounded-full bg-[#F1F4ED] hover:bg-[#E5E7E0] inline-flex items-center justify-center transition-colors"
                aria-label="Chiudi"
              >
                <X size={14} weight="bold" />
              </button>
            </div>
            <div className="px-3 py-3 space-y-1.5">
              <button
                onClick={() => { setMoreOpen(false); shareBeforeAfter(); }}
                className="w-full flex items-start gap-3 p-3 hover:bg-[#F1F4ED] rounded-lg text-left transition-colors"
                data-testid="foto-more-card"
              >
                <span className="w-9 h-9 shrink-0 rounded-full bg-[#7C8B6E]/15 flex items-center justify-center">
                  <ArrowsHorizontal size={16} weight="bold" className="text-[#7C8B6E]" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-semibold text-[#1C1C1A]">Card Prima/Dopo</span>
                  <span className="block text-[11.5px] text-[#6B6A65] mt-0.5">Immagine 1080×1350 pronta per WhatsApp e Instagram</span>
                </span>
              </button>
              <button
                onClick={() => { setMoreOpen(false); shareBeforeAfterVideo(); }}
                className="w-full flex items-start gap-3 p-3 hover:bg-[#F1F4ED] rounded-lg text-left transition-colors"
                data-testid="foto-more-video"
              >
                <span className="w-9 h-9 shrink-0 rounded-full bg-[#C8A66B]/20 flex items-center justify-center">
                  <Sparkle size={16} weight="bold" className="text-[#C8A66B]" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-semibold text-[#1C1C1A]">Video 8s Prima→Dopo</span>
                  <span className="block text-[11.5px] text-[#6B6A65] mt-0.5">MP4 per Reels, Stories, TikTok. Generazione ~30-40s.</span>
                </span>
              </button>
              <button
                onClick={() => {
                  setMoreOpen(false);
                  setLampoTitle("");
                  setLampoCity("");
                  setLampoPrice("");
                  setLampoOpen(true);
                }}
                className="w-full flex items-start gap-3 p-3 hover:bg-[#F1F4ED] rounded-lg text-left transition-colors"
                data-testid="foto-more-lampo"
              >
                <span className="w-9 h-9 shrink-0 rounded-full bg-[#1C1C1A]/10 flex items-center justify-center">
                  <Share size={16} weight="bold" className="text-[#1C1C1A]" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-semibold text-[#1C1C1A]">Crea storia social</span>
                  <span className="block text-[11.5px] text-[#6B6A65] mt-0.5">Card editoriale con titolo, città e prezzo</span>
                </span>
              </button>
              <div className="h-px bg-[#EAE7DF] my-1" />
              <button
                onClick={async () => {
                  const id = singleResultItem.result.id;
                  setMoreOpen(false);
                  if (!confirm("Cancellare questa foto? L'azione non è reversibile.")) return;
                  try {
                    await api.delete(`/live/${id}`);
                    toast.success("Foto cancellata");
                    setHistory((prev) => prev.filter((x) => x.id !== `live:${id}`));
                    reset();
                  } catch {
                    toast.error("Errore cancellazione");
                  }
                }}
                className="w-full flex items-start gap-3 p-3 hover:bg-red-50 rounded-lg text-left transition-colors"
                data-testid="foto-more-delete"
              >
                <span className="w-9 h-9 shrink-0 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash size={16} weight="bold" className="text-red-600" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-semibold text-red-600">Elimina foto</span>
                  <span className="block text-[11.5px] text-[#6B6A65] mt-0.5">Rimossa per sempre dalla galleria</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iter 2026-02 — Room picker sheet per multi-foto Arreda.
          Aperto tramite tap sul chip di una tile → l'utente sceglie
          la categoria per quella foto specifica. Chiuso di default. */}
      {roomPickerFor && (() => {
        const target = items.find((x) => x.id === roomPickerFor);
        if (!target) return null;
        return (
          <div
            className="fixed inset-0 z-[70] bg-[#1C1C1A]/60 backdrop-blur-sm flex items-end sm:items-center justify-center px-4 py-6"
            onClick={() => setRoomPickerFor(null)}
            data-testid="foto-room-picker-sheet"
          >
            <div
              className="bg-white w-full max-w-md rounded-2xl overflow-hidden shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-[#E5E7E0] flex items-center gap-3">
                <img src={target.previewUrl} alt="" className="w-12 h-12 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A65]">Che ambiente vuoi creare?</div>
                  <div className="text-[13px] text-[#1C1C1A] truncate">Foto {items.findIndex((x) => x.id === target.id) + 1}</div>
                </div>
                <button
                  onClick={() => setRoomPickerFor(null)}
                  className="w-8 h-8 rounded-full bg-[#F1F4ED] flex items-center justify-center text-[#1C1C1A]"
                  aria-label="Chiudi"
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
              <div className="p-4 grid grid-cols-2 gap-2">
                {ROOM_HINTS.map((r) => {
                  const active = (target.roomHint || "") === r.v;
                  return (
                    <button
                      key={r.v || "auto"}
                      onClick={() => {
                        setItemRoomHint(target.id, r.v);
                        setRoomPickerFor(null);
                      }}
                      className={`text-[13px] py-2.5 px-3 rounded-lg border transition-all ${
                        active
                          ? "bg-[#7C8B6E] text-white border-[#7C8B6E]"
                          : "bg-white text-[#1C1C1A] border-[#E5E7E0] hover:border-[#7C8B6E]"
                      }`}
                      data-testid={`foto-room-picker-${r.v || "auto"}`}
                    >
                      {r.l}
                    </button>
                  );
                })}
              </div>
              <div className="px-4 pb-4 pt-1">
                <button
                  onClick={() => {
                    // Applica la stessa categoria a TUTTE le pending
                    const hint = target.roomHint || "";
                    setItems((prev) => prev.map((it) =>
                      it.status === "pending" ? { ...it, roomHint: hint } : it
                    ));
                    setRoomPickerFor(null);
                    toast.success(
                      `Applicato a tutte: ${(ROOM_HINTS.find((r) => r.v === hint) || { l: "Automatico" }).l}`,
                      { duration: 1800 }
                    );
                  }}
                  className="w-full text-[12px] font-semibold text-[#6B6A65] hover:text-[#1C1C1A] py-2 transition-colors"
                  data-testid="foto-room-picker-apply-all"
                >
                  Applica <strong>«{(ROOM_HINTS.find((r) => r.v === (target.roomHint || "")) || { l: "Automatico" }).l}»</strong> a tutte le foto
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </AppShell>
  );
}
